import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { io } from 'socket.io-client';
import { MapContainer, TileLayer, Marker as LeafletMarker, Popup, Circle, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import Home from './Home';

// API Base URL — dev: localhost:5000, prod: env variable
const API_URL    = process.env.REACT_APP_API_URL    || 'http://localhost:5000/api';
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';
const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:5000';
// If url is already absolute (Cloudinary), return as-is. Otherwise prepend SERVER_URL.
const mediaUrl = (url) => !url ? '' : url.startsWith('http') ? url : `${SERVER_URL}${url}`;

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

// Socket connection
const socket = io(SOCKET_URL);

// Auth Context
const AuthContext = createContext(null);

const api = axios.create({
  baseURL: API_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers['x-auth-token'] = token;
  }
  return config;
});

// ============ COMPONENTS ============

function GoogleSignInButton({ onCredential }) {
  const ref = useRef(null);

  useEffect(() => {
    let attempts = 0;
    const init = () => {
      if (!ref.current) return;
      if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize({ client_id: GOOGLE_CLIENT_ID, callback: onCredential });
        window.google.accounts.id.renderButton(ref.current, {
          theme: 'outline', size: 'large', width: ref.current.offsetWidth || 320, locale: 'iw',
        });
      } else if (++attempts < 30) {
        setTimeout(init, 200);
      }
    };
    init();
  }, [onCredential]);

  return <div ref={ref} style={{ width: '100%', minHeight: 44 }} />;
}


function OAuthDivider() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', margin: '1.25rem 0' }}>
      <hr style={{ flex: 1, borderColor: 'rgba(0,200,240,0.12)' }} />
      <span style={{ padding: '0 1rem', color: '#3a5878', fontSize: '0.75rem', fontFamily: 'Oxanium, monospace', letterSpacing: '2px', textTransform: 'uppercase' }}>או</span>
      <hr style={{ flex: 1, borderColor: 'rgba(0,200,240,0.12)' }} />
    </div>
  );
}

// Navbar Component
function Navbar() {
  const { user, logout } = useContext(AuthContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const close = () => setMenuOpen(false);

  return (
    <nav className="navbar">
      <Link to="/" className="navbar-logo" dir="ltr" onClick={close}>
        <svg viewBox="0 0 36 36" width="22" height="22" fill="none" style={{ flexShrink: 0 }}>
          {/* Arms */}
          <line x1="18" y1="18" x2="6"  y2="6"  stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <line x1="18" y1="18" x2="30" y2="6"  stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <line x1="18" y1="18" x2="6"  y2="30" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <line x1="18" y1="18" x2="30" y2="30" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          {/* Motor rings */}
          <circle cx="6"  cy="6"  r="4" stroke="currentColor" strokeWidth="1.5"/>
          <circle cx="30" cy="6"  r="4" stroke="currentColor" strokeWidth="1.5"/>
          <circle cx="6"  cy="30" r="4" stroke="currentColor" strokeWidth="1.5"/>
          <circle cx="30" cy="30" r="4" stroke="currentColor" strokeWidth="1.5"/>
          {/* Body */}
          <rect x="13" y="13" width="10" height="10" rx="2.5" fill="currentColor"/>
          {/* Camera lens */}
          <circle cx="18" cy="18" r="2.2" fill="#04080f" opacity="0.7"/>
        </svg>
        Sky<strong>Snap</strong>
        <span className="nav-status" title="מערכת פעילה"></span>
      </Link>

      {/* Desktop links */}
      <div className="navbar-links">
        <Link to="/map">מפת צלמים</Link>
        <Link to="/feed">פיד</Link>
        <Link to="/profiles">כל הפרופילים</Link>
        {user ? (
          <>
            <Link to="/dashboard">לוח בקרה</Link>
            <button className="btn btn-outline" onClick={logout}>התנתק</button>
          </>
        ) : (
          <>
            <Link to="/login" className="btn btn-outline">התחברות</Link>
            <Link to="/register" className="btn btn-primary">הרשמה</Link>
          </>
        )}
      </div>

      {/* Hamburger button — mobile only */}
      <button className="hamburger" onClick={() => setMenuOpen(o => !o)} aria-label="תפריט">
        {menuOpen ? '✕' : '☰'}
      </button>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="mobile-menu">
          <Link to="/map"      onClick={close}>מפת צלמים</Link>
          <Link to="/feed"     onClick={close}>פיד</Link>
          <Link to="/profiles" onClick={close}>כל הפרופילים</Link>
          {user ? (
            <>
              <Link to="/dashboard" onClick={close}>לוח בקרה</Link>
              <button onClick={() => { logout(); close(); }}>התנתק</button>
            </>
          ) : (
            <>
              <Link to="/login"    onClick={close}>התחברות</Link>
              <Link to="/register" onClick={close}>הרשמה</Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
}

// Booking Paid — fires when WooCommerce redirects back after payment
function BookingPaidHandler() {
  const [searchParams, setSearchParams] = useSearchParams();
  const bookingPaid = searchParams.get('booking_paid');
  const sid = searchParams.get('sid');
  const [order, setOrder] = useState(null);
  const [show, setShow] = useState(!!bookingPaid);

  useEffect(() => {
    if (!bookingPaid || !sid) return;
    setSearchParams({}, { replace: true });
    api.get(`/orders/${sid}/success`).then(r => {
      setOrder(r.data);
      // Auto-open WhatsApp with the message
      const phone = r.data.photographer?.phone;
      if (phone) {
        const date = r.data.scheduledTime
          ? new Date(r.data.scheduledTime).toLocaleString('he-IL') : 'לא צוין';
        const msg = `שלום ${r.data.photographer?.name}, קיבלת הזמנה חדשה ב-SkySnap! 🚁\n\n` +
          `👤 לקוח: ${r.data.client?.name}\n` +
          `📍 כתובת: ${r.data.location?.address || 'לא צוינה'}\n` +
          `📅 תאריך: ${date}\n` +
          `💬 הערות: ${r.data.notes || 'אין'}\n` +
          `💰 מחיר: ₪${r.data.price}\n\n` +
          `כנס ללוח הבקרה לאישור ההזמנה.`;
        const waNum = phone.replace(/\D/g,'').replace(/^0/,'972');
        window.location.href = `https://wa.me/${waNum}?text=${encodeURIComponent(msg)}`;
      }
    }).catch(() => {});
  }, []); // eslint-disable-line

  if (!show) return null;

  return (
    <div className="modal-overlay" onClick={() => setShow(false)}>
      <div className="modal" style={{ textAlign: 'center', maxWidth: 400 }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: '3.5rem', marginBottom: '1rem', filter: 'drop-shadow(0 0 20px rgba(0,232,135,0.6))' }}>✅</div>
        <h3 style={{ fontFamily: 'Oxanium, monospace', fontWeight: 800, letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--success)', marginBottom: '0.5rem', fontSize: '1rem' }}>
          התשלום הושלם!
        </h3>
        <p style={{ color: 'var(--text-dim)', fontSize: '0.85rem', marginBottom: '1.5rem', lineHeight: 1.7 }}>
          מעביר לוואטסאפ לשליחת פרטי ההזמנה לצלם...
        </p>
        <div className="spinner" style={{ margin: '0 auto' }} />
      </div>
    </div>
  );
}

// Login Page
function Login() {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/auth/login', formData);
      login(res.data.token, res.data.user);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.msg || 'שגיאה בהתחברות');
    }
  };

  const handleGoogleCredential = async (response) => {
    try {
      const res = await api.post('/auth/google', { credential: response.credential });
      login(res.data.token, res.data.user);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.msg || 'שגיאה בהתחברות עם Google');
    }
  };

  return (
    <div className="form-container">
      <h2>התחברות</h2>
      {error && <div className="alert alert-error">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>אימייל</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
          />
        </div>
        <div className="form-group">
          <label>סיסמה</label>
          <input
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            required
          />
        </div>
        <button type="submit" className="btn btn-primary" style={{ width: '100%' }}>
          התחבר
        </button>
      </form>
      <OAuthDivider />
      <GoogleSignInButton onCredential={handleGoogleCredential} />
      <p className="auth-link">אין לך חשבון? <a href="/register">הרשם כאן</a></p>
    </div>
  );
}

// Register Page
function Register() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    role: 'client'
  });
  const [error, setError] = useState('');
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/auth/register', formData);
      login(res.data.token, res.data.user);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.msg || 'שגיאה בהרשמה');
    }
  };

  const handleGoogleCredential = async (response) => {
    try {
      const res = await api.post('/auth/google', { credential: response.credential, role: formData.role });
      login(res.data.token, res.data.user);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.msg || 'שגיאה בהרשמה עם Google');
    }
  };

  return (
    <div className="form-container">
      <h2>הרשמה</h2>
      {error && <div className="alert alert-error">{error}</div>}

      <div className="form-group">
        <label>סוג משתמש</label>
        <select
          value={formData.role}
          onChange={(e) => setFormData({ ...formData, role: e.target.value })}
        >
          <option value="client">לקוח</option>
          <option value="photographer">צלם רחפנים</option>
        </select>
      </div>

      <GoogleSignInButton onCredential={handleGoogleCredential} />

      <OAuthDivider />

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>שם מלא</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
        </div>
        <div className="form-group">
          <label>טלפון</label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label>אימייל</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
          />
        </div>
        <div className="form-group">
          <label>סיסמה</label>
          <input
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            required
          />
        </div>
        <button type="submit" className="btn btn-primary" style={{ width: '100%' }}>
          הרשם עם אימייל
        </button>
      </form>
      <p className="auth-link">כבר יש לך חשבון? <a href="/login">התחבר כאן</a></p>
    </div>
  );
}

// Map Page
// Fix Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Israeli drone flight restriction zones
const droneZones = [
  // PROHIBITED — no fly under any circumstances
  { name: 'שדה התעופה בן גוריון', lat: 32.0055, lng: 34.8854, radius: 5000, type: 'prohibited' },
  { name: 'שדה התעופה חיפה', lat: 32.8094, lng: 35.0430, radius: 3000, type: 'prohibited' },
  { name: 'שדה התעופה רמון (אילת)', lat: 29.7239, lng: 35.0132, radius: 3000, type: 'prohibited' },
  { name: 'שדה התעופה תל נוף', lat: 31.8395, lng: 34.8218, radius: 4000, type: 'prohibited' },
  { name: 'שדה התעופה חצור', lat: 31.7634, lng: 34.7272, radius: 3000, type: 'prohibited' },
  { name: 'שדה התעופה עובדה (מצפה רמון)', lat: 30.7524, lng: 34.8057, radius: 3000, type: 'prohibited' },
  { name: 'מתקן גרעיני דימונה', lat: 31.0050, lng: 35.1436, radius: 6000, type: 'prohibited' },
  { name: 'שדה התעופה ירושלים', lat: 31.8647, lng: 35.2192, radius: 3000, type: 'prohibited' },
  // RESTRICTED — requires permit
  { name: 'מרכז תל אביב (מגבלת גובה)', lat: 32.0853, lng: 34.7818, radius: 3500, type: 'restricted' },
  { name: 'ירושלים העיר העתיקה', lat: 31.7767, lng: 35.2345, radius: 2000, type: 'restricted' },
  { name: 'שמורת הכרמל', lat: 32.7200, lng: 35.0200, radius: 4000, type: 'restricted' },
  { name: 'אזור תעשייה צבאי קריית גת', lat: 31.6100, lng: 34.7700, radius: 3000, type: 'restricted' },
  // MILITARY — prohibited / restricted
  { name: 'בסיס חיל האוויר חצרים', lat: 31.2329, lng: 34.6609, radius: 4000, type: 'prohibited' },
  { name: 'בסיס חיל האוויר נבטים', lat: 31.2082, lng: 35.0120, radius: 4000, type: 'prohibited' },
  { name: 'בסיס חיל האוויר פלמחים', lat: 31.8967, lng: 34.6906, radius: 4000, type: 'prohibited' },
  { name: 'בסיס חיל האוויר רמת דוד', lat: 32.6650, lng: 35.1789, radius: 4000, type: 'prohibited' },
  { name: 'הקריה — משרד הביטחון ת"א', lat: 32.0699, lng: 34.7961, radius: 2000, type: 'prohibited' },
  { name: 'בסיס צבאי סדות מיכה', lat: 31.7093, lng: 34.9277, radius: 3000, type: 'prohibited' },
  { name: 'בסיס שדה דב (אזור צבאי)', lat: 32.1013, lng: 34.7861, radius: 2500, type: 'prohibited' },
  { name: 'שמיר — מתקן ביטחוני', lat: 33.1700, lng: 35.6200, radius: 3000, type: 'restricted' },
  { name: 'אזור אימונים — בקעת הירדן דרום', lat: 32.1000, lng: 35.5000, radius: 6000, type: 'restricted' },
  { name: 'אזור אימונים — נגב צפוני', lat: 31.1000, lng: 34.7000, radius: 8000, type: 'restricted' },
  // Beit She'an area — city itself is permitted, zones are WEST (Gilboa) and EAST (Jordan border)
  { name: 'אזור גלבוע — אזור אימונים צבאי (מערב בית שאן)', lat: 32.4500, lng: 35.3600, radius: 6000, type: 'restricted' },
  { name: 'אזור ביטחוני — גדת הירדן (מזרח בית שאן)', lat: 32.5000, lng: 35.5800, radius: 4000, type: 'prohibited' },
  { name: 'אזור ביטחוני — מעבר גשר אלנבי', lat: 31.8800, lng: 35.5400, radius: 3500, type: 'prohibited' },
  { name: 'אזור ביטחוני — מעבר גשר אדם (דמיה)', lat: 32.1100, lng: 35.5500, radius: 3000, type: 'prohibited' },
  { name: 'שדה התעופה מגידו (צבאי)', lat: 32.5973, lng: 35.2289, radius: 3000, type: 'prohibited' },
  // DANGEROUS — caution required
  { name: 'גבול לבנון — אזור סכנה', lat: 33.0700, lng: 35.1000, radius: 5000, type: 'dangerous' },
  { name: 'גבול עזה — אזור סכנה', lat: 31.3500, lng: 34.4800, radius: 6000, type: 'dangerous' },
  { name: 'גבול סוריה — רמת הגולן', lat: 33.0000, lng: 35.7700, radius: 5000, type: 'dangerous' },
];

const zoneStyles = {
  prohibited: { color: '#ef4444', fillColor: '#ef4444', fillOpacity: 0.15 },
  restricted:  { color: '#f97316', fillColor: '#f97316', fillOpacity: 0.12 },
  dangerous:   { color: '#eab308', fillColor: '#eab308', fillOpacity: 0.12 },
};

const zoneLabels = {
  prohibited: '🚫 אסור לטיסה',
  restricted:  '⚠️ מוגבל — נדרש היתר',
  dangerous:   '⚡ מסוכן — יש להיזהר',
};

// Custom map icons
const photographerIcon = typeof L !== 'undefined' ? L.divIcon({
  html: `<div style="background:linear-gradient(135deg,#00d4ff,#0055cc);width:38px;height:38px;border-radius:50%;border:2.5px solid rgba(0,212,255,0.5);box-shadow:0 0 14px rgba(0,212,255,0.45),0 2px 8px rgba(0,0,0,0.6);display:flex;align-items:center;justify-content:center;">
    <svg viewBox="0 0 36 36" width="22" height="22" fill="none">
      <line x1="18" y1="18" x2="6"  y2="6"  stroke="white" stroke-width="2.2" stroke-linecap="round"/>
      <line x1="18" y1="18" x2="30" y2="6"  stroke="white" stroke-width="2.2" stroke-linecap="round"/>
      <line x1="18" y1="18" x2="6"  y2="30" stroke="white" stroke-width="2.2" stroke-linecap="round"/>
      <line x1="18" y1="18" x2="30" y2="30" stroke="white" stroke-width="2.2" stroke-linecap="round"/>
      <circle cx="6"  cy="6"  r="4" stroke="white" stroke-width="1.5"/>
      <circle cx="30" cy="6"  r="4" stroke="white" stroke-width="1.5"/>
      <circle cx="6"  cy="30" r="4" stroke="white" stroke-width="1.5"/>
      <circle cx="30" cy="30" r="4" stroke="white" stroke-width="1.5"/>
      <rect x="13" y="13" width="10" height="10" rx="2.5" fill="white"/>
    </svg>
  </div>`,
  className: '', iconSize: [38, 38], iconAnchor: [19, 19]
}) : null;

const postIcon = typeof L !== 'undefined' ? L.divIcon({
  html: `<div style="background:linear-gradient(135deg,#f97316,#ef4444);width:30px;height:30px;border-radius:50%;border:2px solid rgba(255,255,255,0.4);box-shadow:0 0 10px rgba(239,68,68,0.45),0 2px 6px rgba(0,0,0,0.5);display:flex;align-items:center;justify-content:center;">
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="white" stroke-width="1.5">
      <path stroke-linecap="round" stroke-linejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316Z"/>
      <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zm2.25-1.75h.008v.008H18.75V11z"/>
    </svg>
  </div>`,
  className: '', iconSize: [30, 30], iconAnchor: [15, 15]
}) : null;

function MapPage() {
  const [photographers, setPhotographers] = useState([]);
  const [feedPosts, setFeedPosts] = useState([]);
  const [selectedPhotographer, setSelectedPhotographer] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [legendCollapsed, setLegendCollapsed] = useState(true);
  const [showBooking, setShowBooking] = useState(false);
  const [bookingData, setBookingData] = useState({ address: '', scheduledTime: '', notes: '' });
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const defaultCenter = [31.0461, 34.8516]; // Israel center

  useEffect(() => {
    loadPhotographers();
    api.get('/feed/map').then(r => setFeedPosts(r.data)).catch(() => {});
    
    socket.on('photographerLocationUpdate', (data) => {
      setPhotographers(prev => prev.map(p => {
        if (p._id === data.photographerId) {
          return { ...p, currentLocation: { lat: data.lat, lng: data.lng, lastUpdated: data.lastUpdated } };
        }
        return p;
      }));
    });

    socket.on('photographerAvailabilityUpdate', (data) => {
      setPhotographers(prev => prev.map(p => {
        if (p._id === data.photographerId) {
          return { ...p, isAvailable: data.isAvailable };
        }
        return p;
      }));
    });

    return () => {
      socket.off('photographerLocationUpdate');
      socket.off('photographerAvailabilityUpdate');
    };
  }, []);

  const loadPhotographers = async () => {
    try {
      const res = await api.get('/photographers/available');
      setPhotographers(res.data);
    } catch (err) {
      console.error('Error loading photographers:', err);
    }
  };

  const handleBooking = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      const lat = 31.0461 + (Math.random() - 0.5) * 0.1;
      const lng = 34.8516 + (Math.random() - 0.5) * 0.1;
      
      await api.post('/orders', {
        photographerId: selectedPhotographer._id,
        location: {
          address: bookingData.address,
          lat,
          lng
        },
        scheduledTime: bookingData.scheduledTime,
        notes: bookingData.notes
      });
      
      alert('ההזמנה נשלחה בהצלחה!');
      setShowBooking(false);
      navigate('/dashboard');
    } catch (err) {
      alert('שגיאה בשליחת ההזמנה');
    }
  };

  return (
    <div className="map-container">
      {/* Map legend */}
      <div className="map-legend" style={{
        position: 'absolute', bottom: 32, left: 16, zIndex: 1000,
        minWidth: legendCollapsed ? 0 : 178, overflow: 'hidden',
        transition: 'all 0.2s ease', direction: 'rtl', fontSize: 13,
      }}>
        <div onClick={() => setLegendCollapsed(c => !c)} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
          padding: '9px 13px', cursor: 'pointer', userSelect: 'none'
        }}>
          <span style={{ fontWeight: 600, fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: 1, fontFamily: 'var(--f-body)' }}>מקרא מפה</span>
          <span style={{ fontSize: 9, color: 'var(--text-dim)', transform: legendCollapsed ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', lineHeight: 1 }}>▲</span>
        </div>

        {!legendCollapsed && (
          <div style={{ padding: '0 13px 12px', display: 'flex', flexDirection: 'column', gap: 7 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ background: 'linear-gradient(135deg,#00d4ff,#0055cc)', width: 28, height: 28, borderRadius: '50%', border: '2px solid rgba(0,212,255,0.4)', boxShadow: '0 0 10px rgba(0,212,255,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg viewBox="0 0 36 36" width="16" height="16" fill="none">
                  <line x1="18" y1="18" x2="6"  y2="6"  stroke="white" stroke-width="2.2" stroke-linecap="round"/>
                  <line x1="18" y1="18" x2="30" y2="6"  stroke="white" stroke-width="2.2" stroke-linecap="round"/>
                  <line x1="18" y1="18" x2="6"  y2="30" stroke="white" stroke-width="2.2" stroke-linecap="round"/>
                  <line x1="18" y1="18" x2="30" y2="30" stroke="white" stroke-width="2.2" stroke-linecap="round"/>
                  <circle cx="6"  cy="6"  r="4" stroke="white" stroke-width="1.5"/>
                  <circle cx="30" cy="6"  r="4" stroke="white" stroke-width="1.5"/>
                  <circle cx="6"  cy="30" r="4" stroke="white" stroke-width="1.5"/>
                  <circle cx="30" cy="30" r="4" stroke="white" stroke-width="1.5"/>
                  <rect x="13" y="13" width="10" height="10" rx="2.5" fill="white"/>
                </svg>
              </div>
              <span style={{ color: 'var(--text)', fontSize: 12 }}>צלם רחפן זמין</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ background: 'linear-gradient(135deg,#f97316,#ef4444)', width: 24, height: 24, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.2)', boxShadow: '0 0 8px rgba(239,68,68,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="white" stroke-width="1.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316Z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z"/>
                </svg>
              </div>
              <span style={{ color: 'var(--text)', fontSize: 12 }}>עבודה שצולמה כאן</span>
            </div>
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 7, marginTop: 2 }}>
              <div style={{ fontWeight: 600, fontSize: 10, color: 'var(--text-dim)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1, fontFamily: 'var(--f-body)' }}>אזורי טיסה</div>
              {[['#ef4444','אסור לטיסה'],['#f97316','מוגבל — נדרש היתר'],['#eab308','מסוכן — יש להיזהר']].map(([color, label]) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
                  <div style={{ width: 12, height: 12, borderRadius: '50%', background: color, flexShrink: 0 }} />
                  <span style={{ color: 'var(--text)', fontSize: 11.5 }}>{label}</span>
                </div>
              ))}
              <a href="https://www.icd.org.il/noflyzones" target="_blank" rel="noreferrer" style={{ fontSize: 10, color: 'var(--primary)', display: 'block', marginTop: 5 }}>מפת אזורים רשמית: ICD ←</a>
            </div>
          </div>
        )}
      </div>
      <MapContainer center={defaultCenter} zoom={8} style={{ height: '100%', width: '100%' }}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> | אזורי טיסה לפי CAAI — לבדיקה רשמית: <a href="https://www.icd.org.il/noflyzones" target="_blank">gov.il/caai</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {/* Drone flight restriction zones */}
        {droneZones.map(z => (
          <Circle
            key={z.name}
            center={[z.lat, z.lng]}
            radius={z.radius}
            pathOptions={zoneStyles[z.type]}
          >
            <Tooltip sticky>{zoneLabels[z.type]} — {z.name}</Tooltip>
          </Circle>
        ))}
        {/* Photographer markers */}
        {photographers.map(p => (
          p.currentLocation?.lat && (
            <LeafletMarker
              key={p._id}
              position={[p.currentLocation.lat, p.currentLocation.lng]}
              icon={photographerIcon}
              eventHandlers={{ click: () => setSelectedPhotographer(p) }}
            >
              <Popup>
                <div style={{ minWidth: 210, direction: 'rtl', fontFamily: 'var(--f-body)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, paddingBottom: 10, borderBottom: '1px solid var(--border)' }}>
                    <div style={{ width: 44, height: 44, borderRadius: '50%', overflow: 'hidden', background: 'var(--surface-3)', flexShrink: 0, position: 'relative', border: '2px solid var(--primary)', boxShadow: '0 0 8px rgba(0,212,255,0.2)' }}>
                      {p.userId?.profileImage
                        ? <img src={mediaUrl(p.userId.profileImage)} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)', fontWeight: 700, fontSize: 18 }}>{p.userId?.name?.charAt(0)}</div>}
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-bright)', fontFamily: 'var(--f-display)' }}>{p.userId?.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>{p.droneModel || 'צלם רחפנים'}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 12, color: 'var(--text)' }}>
                    <span>★ {p.rating?.toFixed(1) || '—'}</span>
                    <span style={{ color: 'var(--primary)', fontWeight: 700 }}>₪{p.pricePerJob || '—'}</span>
                    <span>{p.averageDeliveryTime || 30} דק'</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {(() => {
                        const phone = p.userId?.phone || feedPosts.find(fp => fp.photographer?._id === p._id)?.photographer?.phone;
                        return phone ? (
                          <a href={`https://wa.me/${phone.replace(/\D/g,'').replace(/^0/,'972')}?text=${encodeURIComponent(`שלום ${p.userId?.name}, ראיתי את הפרופיל שלך ב-SkySnap ואני מעוניין להזמין צילום רחפן 🚁`)}`}
                            target="_blank" rel="noreferrer"
                            style={{ flex: 1, background: '#25d366', color: '#fff', borderRadius: 8, padding: '8px 6px', fontWeight: 700, fontSize: 12, textAlign: 'center', textDecoration: 'none', display: 'block' }}>
                            WhatsApp
                          </a>
                        ) : (
                          <span style={{ flex: 1, background: 'var(--surface-3)', color: 'var(--text-dim)', borderRadius: 8, padding: '8px 6px', fontWeight: 600, fontSize: 12, textAlign: 'center', display: 'block' }}>
                            WhatsApp
                          </span>
                        );
                      })()}
                      <button onClick={() => navigate(`/profile/${p._id}`)}
                        style={{ flex: 1, background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 6px', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
                        פרופיל
                      </button>
                    </div>
                    <button onClick={() => { setSelectedPhotographer(p); setShowBooking(true); }}
                      style={{ background: 'var(--primary)', color: '#001520', border: 'none', borderRadius: 8, padding: '10px 12px', fontWeight: 800, fontSize: 13, cursor: 'pointer', width: '100%' }}>
                      הזמן עכשיו
                    </button>
                  </div>
                </div>
              </Popup>
            </LeafletMarker>
          )
        ))}

        {/* Feed post markers */}
        {feedPosts.map(post => (
          <LeafletMarker
            key={post._id}
            position={[post.location.lat, post.location.lng]}
            icon={postIcon}
            eventHandlers={{ click: () => setSelectedPost(post) }}
          >
            <Popup>
              <div style={{ minWidth: 210, direction: 'rtl', fontFamily: 'var(--f-body)' }}>
                {post.type === 'video'
                  ? <video src={mediaUrl(post.url)} muted style={{ width: '100%', borderRadius: 8, maxHeight: 120, objectFit: 'cover' }} />
                  : <img src={mediaUrl(post.url)} alt={post.title} style={{ width: '100%', borderRadius: 8, maxHeight: 120, objectFit: 'cover' }} />}
                <p style={{ margin: '9px 0 3px', fontWeight: 700, fontSize: 13, color: 'var(--text-bright)', fontFamily: 'var(--f-display)' }}>{post.title || 'ללא כותרת'}</p>
                <p style={{ margin: '2px 0', fontSize: 12, color: 'var(--text)' }}>{post.photographer.name}</p>
                <p style={{ margin: '2px 0', fontSize: 12, color: '#ef4444', display: 'flex', alignItems: 'center', gap: 3 }}>
                  <svg viewBox="0 0 24 24" fill="currentColor" width="11" height="11"><path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z"/></svg>
                  {post.likes}
                </p>
                {post.location.address && <p style={{ margin: '2px 0 8px', fontSize: 11, color: 'var(--text-dim)' }}>{post.location.address.split(',')[0]}</p>}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {post.photographer.phone ? (
                      <a href={`https://wa.me/${post.photographer.phone.replace(/\D/g,'').replace(/^0/,'972')}?text=${encodeURIComponent(`שלום ${post.photographer.name}, ראיתי את העבודות שלך ב-SkySnap ואני מעוניין להזמין צילום רחפן`)}`}
                        target="_blank" rel="noreferrer"
                        style={{ flex: 1, background: '#25d366', color: '#fff', borderRadius: 8, padding: '7px 6px', fontWeight: 700, fontSize: 12, textAlign: 'center', textDecoration: 'none', display: 'block' }}>
                        WhatsApp
                      </a>
                    ) : (
                      <span style={{ flex: 1, background: 'var(--surface-3)', color: 'var(--text-dim)', borderRadius: 8, padding: '7px 6px', fontSize: 12, textAlign: 'center', display: 'block' }}>
                        WhatsApp
                      </span>
                    )}
                    <button onClick={() => navigate(`/profile/${post.photographer._id}`)}
                      style={{ flex: 1, background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 8, padding: '7px 6px', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
                      פרופיל
                    </button>
                  </div>
                  <button onClick={() => navigate(`/profile/${post.photographer._id}`)}
                    style={{ background: 'var(--primary)', color: '#001520', border: 'none', borderRadius: 8, padding: '10px 12px', fontWeight: 800, fontSize: 13, cursor: 'pointer', width: '100%' }}>
                    הזמן עכשיו
                  </button>
                </div>
              </div>
            </Popup>
          </LeafletMarker>
        ))}
      </MapContainer>

      {showBooking && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>הזמנת צלם</h3>
              <button className="modal-close" onClick={() => setShowBooking(false)}>×</button>
            </div>
            <div className="form-group">
              <label>כתובת</label>
              <input
                type="text"
                value={bookingData.address}
                onChange={(e) => setBookingData({ ...bookingData, address: e.target.value })}
                placeholder="הכנס את הכתובת לצילום"
              />
            </div>
            <div className="form-group">
              <label>תאריך ושעה</label>
              <input
                type="datetime-local"
                value={bookingData.scheduledTime}
                onChange={(e) => setBookingData({ ...bookingData, scheduledTime: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>הערות</label>
              <textarea
                value={bookingData.notes}
                onChange={(e) => setBookingData({ ...bookingData, notes: e.target.value })}
                placeholder="פרטים נוספים..."
                rows="3"
              />
            </div>
            <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleBooking}>
              שלח הזמנה
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ FEED ============

function timeAgo(date) {
  const diff = (Date.now() - new Date(date)) / 1000;
  if (diff < 60) return 'עכשיו';
  if (diff < 3600) return `לפני ${Math.floor(diff / 60)} דק'`;
  if (diff < 86400) return `לפני ${Math.floor(diff / 3600)} שע'`;
  return `לפני ${Math.floor(diff / 86400)} ימים`;
}

function VideoPost({ url }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) el.play().catch(() => {});
      else el.pause();
    }, { threshold: 0.6 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return <video ref={ref} src={url} muted loop playsInline style={{ width: '100%', display: 'block', maxHeight: 780, objectFit: 'cover' }} />;
}

function PostCard({ post, onLike, onComment, user }) {
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(post.likes || 0);
  const [comments, setComments] = useState(post.comments || []);
  const [commentText, setCommentText] = useState('');
  const [showAllComments, setShowAllComments] = useState(false);
  const [showHeart, setShowHeart] = useState(false);
  const lastTap = useRef(0);

  const handleLike = () => {
    if (liked) return;
    setLiked(true);
    setLikes(l => l + 1);
    onLike();
  };

  const handleDoubleTap = () => {
    const now = Date.now();
    if (now - lastTap.current < 300) {
      handleLike();
      setShowHeart(true);
      setTimeout(() => setShowHeart(false), 800);
    }
    lastTap.current = now;
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    const text = commentText;
    setCommentText('');
    const result = await onComment(text);
    if (result) setComments(result);
  };

  const p = post.photographer;
  const initials = p.name?.charAt(0)?.toUpperCase() || '📷';

  return (
    <div className="post-card-v2">
      {/* Header */}
      <div className="post-header">
        <div className="post-avatar">
          {p.profileImage
            ? <img src={mediaUrl(p.profileImage)} alt={p.name} />
            : initials}
        </div>
        <div className="post-meta">
          <div className="post-author">{p.name}</div>
          {p.droneModel && <div className="post-sub">{p.droneModel}</div>}
        </div>
        <div className="post-time">{timeAgo(post.createdAt)}</div>
      </div>

      {post.location?.address && (
        <div className="post-location-tag">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="12" height="12"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"/></svg>
          {post.location.address.split(',')[0]}
        </div>
      )}

      {/* Media */}
      <div className="post-media" onClick={handleDoubleTap}>
        {post.type === 'video'
          ? <VideoPost url={mediaUrl(post.url)} />
          : <img src={mediaUrl(post.url)} alt={post.title} />}
        {showHeart && <div className="post-heart-pop">❤️</div>}
      </div>

      {/* Actions */}
      <div className="post-actions-v2">
        <button className={`post-action-btn-v2${liked ? ' liked' : ''}`} onClick={handleLike} aria-label="לייק">
          {liked ? (
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z"/></svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"/></svg>
          )}
        </button>
        <button className="post-action-btn-v2" aria-label="תגובה" onClick={() => document.getElementById(`comment-input-${post._id}`)?.focus()}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023 2.273 5.48.432.447.74 1.04.586 1.641a4.483 4.483 0 01-.923 1.785A5.969 5.969 0 006 21c1.282 0 2.47-.402 3.445-1.087.81.22 1.668.337 2.555.337z"/></svg>
        </button>
        <div style={{ flex: 1 }} />
        <button className="post-action-btn-v2" aria-label="שמור">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z"/></svg>
        </button>
      </div>

      {/* Likes */}
      <div className="post-likes">{likes.toLocaleString()} לייקים</div>

      {/* Caption */}
      {(post.title || post.description) && (
        <div className="post-caption">
          <span className="post-caption-author">{p.name} </span>
          {post.title}{post.description ? ` — ${post.description}` : ''}
        </div>
      )}

      {/* Comments */}
      {comments.length > 0 && (
        <div className="post-comments">
          {!showAllComments && comments.length > 2 && (
            <button className="post-see-all-comments" onClick={() => setShowAllComments(true)}>
              הצג את כל {comments.length} התגובות
            </button>
          )}
          {(showAllComments ? comments : comments.slice(-2)).map((c, i) => (
            <div key={i} className="post-comment">
              <span className="post-comment-author">{c.userName} </span>{c.text}
            </div>
          ))}
        </div>
      )}

      {/* Comment input */}
      {user && (
        <form className="post-comment-form" onSubmit={submitComment}>
          <input
            id={`comment-input-${post._id}`}
            className="post-comment-input"
            value={commentText}
            onChange={e => setCommentText(e.target.value)}
            placeholder="הוסף תגובה..."
          />
          {commentText.trim() && (
            <button type="submit" className="post-comment-submit">פרסם</button>
          )}
        </form>
      )}
    </div>
  );
}

function StoriesBar({ photographers }) {
  if (!photographers.length) return null;
  return (
    <div className="stories-bar">
      {photographers.map(p => (
        <Link key={p._id} to={`/profile/${p._id}`} className="story-item" style={{ textDecoration: 'none' }}>
          <div className="story-ring-v2">
            <div className="story-inner-v2">
              {p.profileImage
                ? <img src={mediaUrl(p.profileImage)} alt={p.name} />
                : p.name?.charAt(0)?.toUpperCase()}
            </div>
          </div>
          <span className="story-name">{p.name?.split(' ')[0]}</span>
        </Link>
      ))}
    </div>
  );
}

function FeedPage() {
  const [posts, setPosts] = useState([]);
  const [photographers, setPhotographers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useContext(AuthContext);
  const location = useLocation();

  useEffect(() => {
    Promise.all([
      api.get('/feed'),
      api.get('/photographers')
    ]).then(([feedRes, photRes]) => {
      setPosts(feedRes.data);
      setPhotographers(photRes.data.map(p => ({ _id: p._id, name: p.userId?.name, profileImage: p.userId?.profileImage })));
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleLike = async (postId) => {
    try { await api.post(`/photographers/portfolio/${postId}/like`); } catch {}
  };

  const handleComment = async (postId, text) => {
    try {
      const res = await api.post(`/photographers/portfolio/${postId}/comment`, { text });
      setPosts(prev => prev.map(p => p._id === postId ? { ...p, comments: res.data.comments } : p));
      return res.data.comments;
    } catch { return null; }
  };

  if (loading) return (
    <div className="loading">
      <span className="loading-drone">🚁</span>
      <span>טוען פיד...</span>
    </div>
  );

  const navLinks = [
    { to: '/feed',       label: 'פיד',             icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"/></svg> },
    { to: '/map',        label: 'מפת צלמים',        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z"/></svg> },
    { to: '/profiles',   label: 'כל הצלמים',        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"/></svg> },
  ];

  const accountLinks = user ? [
    { to: '/dashboard',  label: 'לוח בקרה',         icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z"/></svg> },
    { to: '/dashboard',  label: 'הגדרות חשבון',      icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"/></svg> },
    ...(user?.role === 'photographer' ? [
      { to: '/dashboard', label: 'הפוסטים שלי',      icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"/></svg> },
      { to: '/dashboard', label: 'העלאת תוכן',        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"/></svg> },
    ] : []),
  ] : [
    { to: '/login',      label: 'התחברות',           icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75"/></svg> },
    { to: '/register',   label: 'הרשמה',             icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zM4 19.235v-.11a6.375 6.375 0 0112.75 0v.109A12.318 12.318 0 0110.374 21c-2.331 0-4.512-.645-6.374-1.766z"/></svg> },
  ];

  return (
    <div className="feed-layout">
      <nav className="feed-nav-panel">
        <div className="feed-nav-section-title">ניווט</div>
        {navLinks.map(({ to, label, icon }) => (
          <Link key={label} to={to} className={`feed-nav-link${location.pathname === to ? ' active' : ''}`}>
            {icon}{label}
          </Link>
        ))}
        <div className="feed-nav-divider" />
        <div className="feed-nav-section-title">החשבון</div>
        {accountLinks.map(({ to, label, icon }) => (
          <Link key={label} to={to} className="feed-nav-link">
            {icon}{label}
          </Link>
        ))}
      </nav>
      <main className="feed-main">
        <StoriesBar photographers={photographers} />
        {posts.length === 0 ? (
          <div className="feed-empty">
            <span className="feed-empty-icon">🚁</span>
            <p style={{ fontWeight: 600, marginBottom: 6 }}>עדיין אין פוסטים בפיד</p>
            <p style={{ fontSize: 13 }}>צלמים שמעלים תמונות וסרטונים יופיעו כאן</p>
          </div>
        ) : (
          posts.map(post => (
            <PostCard
              key={post._id}
              post={post}
              onLike={() => handleLike(post._id)}
              onComment={(text) => handleComment(post._id, text)}
              user={user}
            />
          ))
        )}
      </main>
      {photographers.length > 0 && (
        <aside className="feed-sidebar-panel">
          <div className="feed-sidebar-widget">
            <div className="sidebar-widget-title">צלמים מובילים</div>
            {photographers.slice(0, 6).map(p => (
              <Link key={p._id} to={`/profile/${p._id}`} style={{ textDecoration: 'none' }}>
                <div className="suggested-user">
                  <div className="sug-avatar">
                    {p.profileImage
                      ? <img src={mediaUrl(p.profileImage)} alt={p.name} />
                      : p.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div className="sug-info">
                    <div className="sug-name">{p.name}</div>
                    <div className="sug-sub">צלם רחפנים</div>
                  </div>
                  <span className="sug-btn">פרופיל</span>
                </div>
              </Link>
            ))}
          </div>
        </aside>
      )}
    </div>
  );
}

function SettingsPage_REMOVED() {
  const { user, login } = useContext(AuthContext);
  const [tab, setTab] = useState('profile');
  const [profileData, setProfileData] = useState({ name: user?.name || '', bio: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [photographerProfile, setPhotographerProfile] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [editingLocation, setEditingLocation] = useState(null); // { id, address, lat, lng }
  const [uploadData, setUploadData] = useState({ title: '', description: '', type: 'photo' });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [geocodingAddress, setGeocodingAddress] = useState(false);
  const [geocodeResults, setGeocodeResults] = useState([]);
  const geocodeSetterRef = useRef(null);
  const avatarInputRef = useRef(null);

  useEffect(() => {
    api.get('/auth/me').then(res => {
      setProfileData({ name: res.data.name || '', bio: res.data.bio || '', phone: res.data.phone || '' });
    }).catch(() => {});
    if (user?.role === 'photographer') loadPhotographerProfile();
  }, []);

  const loadPhotographerProfile = async () => {
    try {
      const res = await api.get('/photographers');
      const mine = res.data.find(p => p.userId?._id === user?.id || p.userId === user?.id);
      setPhotographerProfile(mine);
    } catch {}
  };

  const showSuccess = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };
  const showError = (msg) => { setError(msg); setTimeout(() => setError(''), 4000); };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const res = await api.put('/users/profile', profileData);
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      const updated = { ...stored, name: res.data.name, bio: res.data.bio, phone: res.data.phone };
      localStorage.setItem('user', JSON.stringify(updated));
      login(localStorage.getItem('token'), updated);
      showSuccess('הפרופיל עודכן בהצלחה!');
    } catch { showError('שגיאה בשמירה'); }
    finally { setSaving(false); }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const form = new FormData();
    form.append('image', file);
    try {
      const res = await api.post('/users/profile-image', form);
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      const updated = { ...stored, profileImage: res.data.profileImage };
      localStorage.setItem('user', JSON.stringify(updated));
      login(localStorage.getItem('token'), updated);
      showSuccess('תמונת הפרופיל עודכנה!');
    } catch { showError('שגיאה בהעלאת התמונה'); }
  };

  const detectLocation = () => {
    if (!navigator.geolocation) return showError('הדפדפן לא תומך ב-GPS');
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        let address = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
        try {
          const r = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`);
          const d = await r.json();
          address = d.display_name?.split(',').slice(0, 3).join(', ') || address;
        } catch {}
        setUploadData(u => ({ ...u, lat, lng, address }));
        setGettingLocation(false);
      },
      () => { showError('לא ניתן לקבל מיקום'); setGettingLocation(false); }
    );
  };

  const geocodeAddress = async (addressText, setter) => {
    if (!addressText?.trim()) return;
    setGeocodingAddress(true);
    geocodeSetterRef.current = setter;
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(addressText)}&format=json&limit=5&countrycodes=il&addressdetails=1`);
      const d = await r.json();
      if (!d.length) { showError('הכתובת לא נמצאה, נסה ניסוח אחר'); setGeocodeResults([]); }
      else if (d.length === 1) { const { lat, lon, display_name } = d[0]; setter(parseFloat(lat), parseFloat(lon), display_name.split(',').slice(0, 4).join(', ')); setGeocodeResults([]); }
      else { setGeocodeResults(d); }
    } catch { showError('שגיאה בחיפוש כתובת'); }
    finally { setGeocodingAddress(false); }
  };

  const pickGeoResult = (result) => {
    const { lat, lon, display_name } = result;
    geocodeSetterRef.current?.(parseFloat(lat), parseFloat(lon), display_name.split(',').slice(0, 4).join(', '));
    setGeocodeResults([]);
  };

  const uploadMedia = async (e) => {
    e.preventDefault();
    if (!uploadFile) return;
    if (uploadFile.size > 200 * 1024 * 1024) {
      showError('הקובץ גדול מדי — מקסימום 200MB לסרטון');
      return;
    }
    if (!uploadData.lat || !uploadData.lng) {
      showError('נדרש מיקום הצילום — לחץ על GPS או הכנס כתובת עם קואורדינטות');
      return;
    }
    setUploading(true);
    try {
      // Step 1: get presigned URL (small request through tunnel)
      const { data: { signedUrl, publicUrl } } = await api.post('/upload/r2-signed-url', {
        filename: uploadFile.name,
        contentType: uploadFile.type || 'application/octet-stream',
      });
      // Step 2: PUT directly to R2 — bypasses tunnel, no size limit
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', signedUrl);
        xhr.setRequestHeader('Content-Type', uploadFile.type || 'application/octet-stream');
        xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`R2 error: ${xhr.status}`));
        xhr.onerror = () => reject(new Error('שגיאת רשת בהעלאה לאחסון'));
        xhr.send(uploadFile);
      });
      // Step 3: save record
      await api.post('/photographers/portfolio/save', {
        url: publicUrl,
        type: uploadData.type,
        title: uploadData.title,
        description: uploadData.description,
        lat: uploadData.lat,
        lng: uploadData.lng,
        address: uploadData.address || '',
      });
      setUploadFile(null);
      setUploadData({ title: '', description: '', type: 'photo', lat: null, lng: null, address: '' });
      await loadPhotographerProfile();
      setTab('media');
      showSuccess('הועלה בהצלחה לפיד!');
    } catch (err) { showError('שגיאה בהעלאה: ' + (err.response?.data?.msg || err.message)); }
    finally { setUploading(false); }
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm('למחוק את הפוסט?')) return;
    try {
      await api.delete(`/photographers/portfolio/${itemId}`);
      await loadPhotographerProfile();
      showSuccess('נמחק');
    } catch { showError('שגיאה במחיקה'); }
  };

  const saveItemEdit = async () => {
    try {
      await api.put(`/photographers/portfolio/${editingItem.id}`, { title: editingItem.title, description: editingItem.description });
      setEditingItem(null);
      await loadPhotographerProfile();
      showSuccess('נשמר!');
    } catch { showError('שגיאה בשמירה'); }
  };

  const saveLocation = async () => {
    if (!editingLocation.lat || !editingLocation.lng) { showError('נדרש מיקום תקין'); return; }
    try {
      await api.put(`/photographers/portfolio/${editingLocation.id}/location`, {
        lat: editingLocation.lat, lng: editingLocation.lng, address: editingLocation.address
      });
      setEditingLocation(null);
      await loadPhotographerProfile();
      showSuccess('מיקום עודכן!');
    } catch { showError('שגיאה בשמירת מיקום'); }
  };

  const detectLocationForEdit = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(pos => {
      setEditingLocation(l => ({ ...l, lat: pos.coords.latitude, lng: pos.coords.longitude, address: l.address || `${pos.coords.latitude.toFixed(5)}, ${pos.coords.longitude.toFixed(5)}` }));
    });
  };

  const avatarSrc = user?.profileImage
    ? (mediaUrl(user.profileImage))
    : null;

  const tabs = [
    { id: 'profile', label: '👤 פרופיל' },
    ...(user?.role === 'photographer' ? [
      { id: 'media', label: '🖼️ הפוסטים שלי' },
      { id: 'upload', label: '➕ העלאה' },
    ] : []),
  ];

  const inputStyle = { width: '100%', border: '1px solid #dbdbdb', borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };

  return (
    <div style={{ maxWidth: 680, margin: '2rem auto', padding: '0 1rem 4rem' }}>
      <h2 style={{ marginBottom: '1.5rem', fontWeight: 700 }}>הגדרות</h2>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #efefef', marginBottom: '1.5rem' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            background: 'none', border: 'none', borderBottom: tab === t.id ? '2px solid #262626' : '2px solid transparent',
            padding: '10px 18px', cursor: 'pointer', fontWeight: tab === t.id ? 600 : 400,
            fontSize: 14, color: tab === t.id ? '#262626' : '#8e8e8e', marginBottom: -1, fontFamily: 'inherit'
          }}>{t.label}</button>
        ))}
      </div>

      {success && <div style={{ background: '#d1fae5', color: '#065f46', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>{success}</div>}
      {error   && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>{error}</div>}

      {/* ── Profile Tab ── */}
      {tab === 'profile' && (
        <div>
          {/* Avatar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 28 }}>
            <div onClick={() => avatarInputRef.current?.click()} style={{
              width: 86, height: 86, borderRadius: '50%', cursor: 'pointer', flexShrink: 0,
              background: avatarSrc ? 'transparent' : 'linear-gradient(135deg,#667eea,#764ba2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 30, color: '#fff', fontWeight: 700, overflow: 'hidden', border: '2px solid #efefef'
            }}>
              {avatarSrc
                ? <img src={avatarSrc} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : user?.name?.charAt(0)?.toUpperCase()}
            </div>
            <input ref={avatarInputRef} type="file" accept="image/*" onChange={uploadAvatar} style={{ display: 'none' }} />
            <div>
              <div style={{ fontWeight: 600 }}>{user?.name}</div>
              <button onClick={() => avatarInputRef.current?.click()}
                style={{ background: 'none', border: 'none', color: '#0095f6', fontWeight: 600, fontSize: 13, cursor: 'pointer', padding: '4px 0' }}>
                שנה תמונת פרופיל
              </button>
            </div>
          </div>

          {/* Fields */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {[
              { label: 'שם מלא', key: 'name', type: 'text', placeholder: 'השם שלך' },
              { label: 'ביוגרפיה', key: 'bio', type: 'textarea', placeholder: 'ספר על עצמך...' },
              { label: 'טלפון', key: 'phone', type: 'tel', placeholder: '050-0000000' },
            ].map(({ label, key, type, placeholder }) => (
              <div key={key}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{label}</label>
                {type === 'textarea'
                  ? <textarea value={profileData[key]} rows={3} placeholder={placeholder}
                      onChange={e => setProfileData(p => ({ ...p, [key]: e.target.value }))}
                      style={{ ...inputStyle, resize: 'vertical' }} />
                  : <input type={type} value={profileData[key]} placeholder={placeholder}
                      onChange={e => setProfileData(p => ({ ...p, [key]: e.target.value }))}
                      style={inputStyle} />}
              </div>
            ))}
            <button onClick={saveProfile} disabled={saving}
              style={{ background: saving ? '#b2dffc' : '#0095f6', color: '#fff', border: 'none', borderRadius: 8, padding: '12px', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
              {saving ? 'שומר...' : 'שמור שינויים'}
            </button>
          </div>
        </div>
      )}

      {/* ── My Media Tab ── */}
      {tab === 'media' && user?.role === 'photographer' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontSize: 14, color: '#8e8e8e' }}>{photographerProfile?.portfolio?.length || 0} פוסטים</span>
            <button onClick={() => setTab('upload')}
              style={{ background: '#0095f6', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
              + העלאה חדשה
            </button>
          </div>
          {!photographerProfile?.portfolio?.length ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#8e8e8e' }}>
              <div style={{ fontSize: 48 }}>🖼️</div>
              <p>עדיין לא העלית תוכן</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 3 }}>
              {photographerProfile.portfolio.map(item => (
                <div key={item._id} style={{ position: 'relative', aspectRatio: '1', overflow: 'hidden', background: '#111', borderRadius: 4 }}
                  onMouseEnter={e => e.currentTarget.querySelector('.item-overlay').style.opacity = 1}
                  onMouseLeave={e => e.currentTarget.querySelector('.item-overlay').style.opacity = 0}>
                  {item.type === 'video'
                    ? <video src={mediaUrl(item.url)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <img src={mediaUrl(item.url)} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                  {!item.location?.lat && (
                    <div style={{ position: 'absolute', top: 4, right: 4, background: '#ef4444', color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: 4, padding: '2px 5px', zIndex: 2 }}>
                      ⚠️ חסר מיקום
                    </div>
                  )}
                  <div className="item-overlay" style={{
                    position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, opacity: 0, transition: '.2s'
                  }}>
                    <span style={{ color: '#fff', fontSize: 12 }}>❤️ {item.likes}</span>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => setEditingItem({ id: item._id, title: item.title || '', description: item.description || '' })}
                        style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 12 }}>
                        ✏️ ערוך
                      </button>
                      <button onClick={() => setEditingLocation({ id: item._id, address: item.location?.address || '', lat: item.location?.lat || null, lng: item.location?.lng || null })}
                        style={{ background: item.location?.lat ? 'rgba(34,197,94,0.8)' : 'rgba(239,68,68,0.85)', border: 'none', color: '#fff', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 12 }}>
                        📍
                      </button>
                      <button onClick={() => deleteItem(item._id)}
                        style={{ background: 'rgba(239,68,68,0.85)', border: 'none', color: '#fff', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 12 }}>
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Upload Tab ── */}
      {tab === 'upload' && user?.role === 'photographer' && (
        <form onSubmit={uploadMedia} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div onClick={() => document.getElementById('settings-file-input')?.click()}
            style={{ border: '2px dashed #dbdbdb', borderRadius: 12, padding: '2.5rem', textAlign: 'center', cursor: 'pointer', background: uploadFile ? '#f0f9ff' : '#fafafa' }}>
            {uploadFile ? (
              <>
                <div style={{ fontSize: 40, marginBottom: 8 }}>{uploadData.type === 'video' ? '🎬' : '🖼️'}</div>
                <p style={{ fontWeight: 600, margin: 0 }}>{uploadFile.name}</p>
                <p style={{ fontSize: 12, color: '#8e8e8e', margin: '4px 0 0' }}>{(uploadFile.size / 1024 / 1024).toFixed(1)} MB</p>
              </>
            ) : (
              <>
                <div style={{ fontSize: 48, marginBottom: 8 }}>☁️</div>
                <p style={{ fontWeight: 600, margin: 0 }}>לחץ לבחירת קובץ</p>
                <p style={{ fontSize: 13, color: '#8e8e8e', margin: '4px 0 0' }}>תמונות ווידאו</p>
              </>
            )}
          </div>
          <input id="settings-file-input" type="file" accept="image/*,video/*" style={{ display: 'none' }}
            onChange={e => {
              const f = e.target.files[0];
              if (!f) return;
              setUploadFile(f);
              setUploadData(d => ({ ...d, type: f.type.startsWith('video') ? 'video' : 'photo' }));
            }} />

          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>כותרת</label>
            <input value={uploadData.title} onChange={e => setUploadData(d => ({ ...d, title: e.target.value }))}
              placeholder="כותרת הפוסט" required style={inputStyle} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>תיאור</label>
            <textarea value={uploadData.description} onChange={e => setUploadData(d => ({ ...d, description: e.target.value }))}
              placeholder="תיאור..." rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>📍 מיקום הצילום</label>
            <div style={{ display: 'flex', gap: 6 }}>
              <input value={uploadData.address} onChange={e => setUploadData(d => ({ ...d, address: e.target.value, lat: null, lng: null }))}
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), geocodeAddress(uploadData.address, (lat, lng, addr) => setUploadData(u => ({ ...u, lat, lng, address: addr }))))}
                placeholder="למשל: הרצל 5 תל אביב, או: בית שאן" style={{ ...inputStyle, flex: 1 }} />
              <button type="button" onClick={() => geocodeAddress(uploadData.address, (lat, lng, addr) => setUploadData(u => ({ ...u, lat, lng, address: addr })))} disabled={geocodingAddress}
                style={{ background: '#22c55e', color: '#fff', border: 'none', borderRadius: 8, padding: '0 12px', fontWeight: 600, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                {geocodingAddress ? '...' : '🔍 חפש'}
              </button>
              <button type="button" onClick={detectLocation} disabled={gettingLocation}
                style={{ background: '#0095f6', color: '#fff', border: 'none', borderRadius: 8, padding: '0 12px', fontWeight: 600, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                {gettingLocation ? '...' : '📍 GPS'}
              </button>
            </div>
            {geocodeResults.length > 0 && (
              <div style={{ marginTop: 6, border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', background: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                {geocodeResults.map((r, i) => (
                  <div key={i} onClick={() => { pickGeoResult(r); setUploadData(u => ({ ...u, lat: parseFloat(r.lat), lng: parseFloat(r.lon), address: r.display_name.split(',').slice(0,4).join(', ') })); }} style={{ padding: '9px 12px', fontSize: 13, cursor: 'pointer', borderBottom: i < geocodeResults.length - 1 ? '1px solid #f1f5f9' : 'none', color: '#1e293b', direction: 'rtl' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                    📍 {r.display_name.split(',').slice(0, 4).join(', ')}
                  </div>
                ))}
                <div onClick={() => setGeocodeResults([])} style={{ padding: '7px 12px', fontSize: 12, cursor: 'pointer', color: '#94a3b8', textAlign: 'center', borderTop: '1px solid #f1f5f9' }}>ביטול</div>
              </div>
            )}
            {uploadData.lat && !geocodeResults.length && (
              <div style={{ fontSize: 12, color: '#0095f6', marginTop: 6 }}>
                ✓ מיקום נקלט: {uploadData.address}
              </div>
            )}
          </div>
          <button type="submit" disabled={uploading || !uploadFile}
            style={{ background: uploading || !uploadFile ? '#b2dffc' : '#0095f6', color: '#fff', border: 'none', borderRadius: 8, padding: '13px', fontWeight: 600, fontSize: 15, cursor: 'pointer' }}>
            {uploading ? 'מעלה...' : '⬆️ העלה לפיד'}
          </button>
        </form>
      )}

      {/* ── Edit Location Modal ── */}
      {editingLocation && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setEditingLocation(null)}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 380, maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: 18, fontWeight: 700 }}>📍 עדכן מיקום הצילום</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>כתובת</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input value={editingLocation.address} onChange={e => setEditingLocation(l => ({ ...l, address: e.target.value, lat: null, lng: null }))}
                    onKeyDown={e => e.key === 'Enter' && geocodeAddress(editingLocation.address, (lat, lng, addr) => setEditingLocation(l => ({ ...l, lat, lng, address: addr })))}
                    placeholder="למשל: הרצל 5 תל אביב, או: בית שאן" style={{ ...inputStyle, flex: 1 }} />
                  <button type="button" onClick={() => geocodeAddress(editingLocation.address, (lat, lng, addr) => setEditingLocation(l => ({ ...l, lat, lng, address: addr })))} disabled={geocodingAddress}
                    style={{ background: '#22c55e', color: '#fff', border: 'none', borderRadius: 8, padding: '0 10px', fontWeight: 600, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    {geocodingAddress ? '...' : '🔍'}
                  </button>
                  <button type="button" onClick={detectLocationForEdit}
                    style={{ background: '#0095f6', color: '#fff', border: 'none', borderRadius: 8, padding: '0 10px', fontWeight: 600, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    📍
                  </button>
                </div>
              </div>
              {geocodeResults.length > 0 && (
                <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', background: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                  {geocodeResults.map((r, i) => (
                    <div key={i} onClick={() => { pickGeoResult(r); setEditingLocation(l => ({ ...l, lat: parseFloat(r.lat), lng: parseFloat(r.lon), address: r.display_name.split(',').slice(0,4).join(', ') })); }} style={{ padding: '9px 12px', fontSize: 13, cursor: 'pointer', borderBottom: i < geocodeResults.length - 1 ? '1px solid #f1f5f9' : 'none', color: '#1e293b', direction: 'rtl' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                      📍 {r.display_name.split(',').slice(0, 4).join(', ')}
                    </div>
                  ))}
                  <div onClick={() => setGeocodeResults([])} style={{ padding: '7px 12px', fontSize: 12, cursor: 'pointer', color: '#94a3b8', textAlign: 'center', borderTop: '1px solid #f1f5f9' }}>ביטול</div>
                </div>
              )}
              {editingLocation.lat && !geocodeResults.length && (
                <div style={{ fontSize: 12, color: '#16a34a', background: '#dcfce7', borderRadius: 6, padding: '6px 10px' }}>
                  ✓ {editingLocation.lat.toFixed(5)}, {editingLocation.lng.toFixed(5)}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={saveLocation}
                  style={{ flex: 1, background: '#0095f6', color: '#fff', border: 'none', borderRadius: 8, padding: 12, fontWeight: 600, cursor: 'pointer' }}>
                  שמור מיקום
                </button>
                <button onClick={() => setEditingLocation(null)}
                  style={{ flex: 1, background: '#f1f5f9', color: '#334155', border: 'none', borderRadius: 8, padding: 12, fontWeight: 600, cursor: 'pointer' }}>
                  ביטול
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Modal ── */}
      {editingItem && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setEditingItem(null)}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 380, maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: 18, fontWeight: 700 }}>ערוך פוסט</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>כותרת</label>
                <input value={editingItem.title} onChange={e => setEditingItem(i => ({ ...i, title: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>תיאור</label>
                <textarea value={editingItem.description} onChange={e => setEditingItem(i => ({ ...i, description: e.target.value }))}
                  rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={saveItemEdit}
                  style={{ flex: 1, background: '#0095f6', color: '#fff', border: 'none', borderRadius: 8, padding: 12, fontWeight: 600, cursor: 'pointer' }}>
                  שמור
                </button>
                <button onClick={() => setEditingItem(null)}
                  style={{ flex: 1, background: '#efefef', border: 'none', borderRadius: 8, padding: 12, fontWeight: 600, cursor: 'pointer' }}>
                  ביטול
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Profiles Page - Instagram-like all photographers
function ProfilesPage() {
  const [photographers, setPhotographers] = useState([]);

  useEffect(() => {
    loadPhotographers();
  }, []);

  const loadPhotographers = async () => {
    try {
      const res = await api.get('/photographers');
      setPhotographers(res.data);
    } catch (err) {
      console.error('Error loading photographers:', err);
    }
  };

  return (
    <div className="page-wrap">
      <h2 className="page-title">כל הצלמים</h2>
      <div className="photographers-grid">
        {photographers.map(p => {
          const avatarSrc = p.userId?.profileImage ? mediaUrl(p.userId.profileImage) : null;
          return (
            <div key={p._id} className="prof-card-v2">
              {p.portfolio?.length > 0 ? (
                <div className="prof-card-cover">
                  {[0, 1, 2].map(i => (
                    <div key={i} className="prof-card-cover-item">
                      {p.portfolio[i] ? (
                        p.portfolio[i].type === 'video'
                          ? <video src={mediaUrl(p.portfolio[i].url)} muted />
                          : <img src={mediaUrl(p.portfolio[i].url)} alt="" />
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="prof-card-cover-empty">🚁</div>
              )}
              <div className="prof-card-body">
                <div className="prof-card-avatar">
                  {avatarSrc ? <img src={avatarSrc} alt="" /> : (p.userId?.name?.charAt(0) || '🚁')}
                </div>
                <div className="prof-card-name">{p.userId?.name || 'צלם'}</div>
                <div className="prof-card-model">{p.droneModel || 'צלם רחפנים'}</div>
                <div className="prof-card-stats">
                  {[
                    { val: p.portfolio?.length || 0, label: 'עבודות' },
                    { val: p.rating?.toFixed(1) || '—',  label: 'דירוג' },
                    { val: `₪${p.pricePerJob || '—'}`,   label: 'לעבודה' },
                  ].map(({ val, label }) => (
                    <div key={label} className="prof-card-stat">
                      <span className="prof-card-stat-val">{val}</span>
                      <span className="prof-card-stat-label">{label}</span>
                    </div>
                  ))}
                </div>
                <Link to={`/profile/${p._id}`} className="btn btn-primary" style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                  צפה בפרופיל
                </Link>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============ PHOTOGRAPHER PROFILE PAGE ============

function PhotographerProfilePage() {
  const { id } = useParams();
  const [photographer, setPhotographer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showBooking, setShowBooking] = useState(false);
  const [bookingData, setBookingData] = useState({ address: '', scheduledTime: '', notes: '' });
  const [bookingResult, setBookingResult] = useState(null);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/photographers').then(r => {
      setPhotographer(r.data.find(p => p._id === id) || null);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  const handleBooking = async () => {
    if (!user) { navigate('/login'); return; }
    try {
      const res = await api.post('/orders', {
        photographerId: photographer._id,
        location: { address: bookingData.address, lat: 31.0461, lng: 34.8516 },
        scheduledTime: bookingData.scheduledTime,
        notes: bookingData.notes
      });
      setShowBooking(false);

      // Build WhatsApp URL
      const phone = photographer.userId?.phone;
      let waUrl = null;
      if (phone) {
        const date = bookingData.scheduledTime
          ? new Date(bookingData.scheduledTime).toLocaleString('he-IL') : 'לא צוין';
        const msg =
          `שלום ${photographer.userId?.name}, קיבלת הזמנה חדשה ב-SkySnap! 🚁\n\n` +
          `👤 לקוח: ${user.name}\n` +
          `📍 כתובת: ${bookingData.address || 'לא צוינה'}\n` +
          `📅 תאריך: ${date}\n` +
          `💬 הערות: ${bookingData.notes || 'אין'}\n\n` +
          `כנס ללוח הבקרה לאישור ההזמנה.`;
        const waNum = phone.replace(/\D/g, '').replace(/^0/, '972');
        waUrl = `https://wa.me/${waNum}?text=${encodeURIComponent(msg)}`;
      }

      setBookingResult({ paymentUrl: res.data.paymentUrl, waUrl });
    } catch { alert('שגיאה בשליחת ההזמנה'); }
  };

  if (loading) return <div className="loading"><span className="loading-drone">🚁</span><span>טוען פרופיל...</span></div>;
  if (!photographer) return <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--text-dim)', fontFamily: 'var(--f-body)' }}>צלם לא נמצא</div>;

  const p = photographer;
  const avatarSrc = p.userId?.profileImage
    ? (mediaUrl(p.userId.profileImage))
    : null;

  const inp2 = { width: '100%', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'var(--f-body)', background: 'var(--surface-2)', color: 'var(--text)', marginBottom: 12 };
  const lbl = { display: 'block', fontSize: 11, fontWeight: 600, marginBottom: 6, color: 'var(--text-dim)', letterSpacing: '1px', textTransform: 'uppercase', fontFamily: 'var(--f-body)' };

  return (
    <div style={{ background: 'var(--bg)', minHeight: 'calc(100vh - 60px)' }}>

      {/* ── Header / Hero ── */}
      <div className="page-header">
        <button onClick={() => navigate(-1)} className="btn btn-outline" style={{ position: 'absolute', top: '1.5rem', right: '2rem', fontSize: '0.75rem' }}>
          ← חזרה
        </button>

        <div className="avatar-circle" style={{ width: 100, height: 100, fontSize: 38, margin: '0 auto 1.25rem' }}>
          {avatarSrc ? <img src={avatarSrc} alt="" /> : (p.userId?.name?.charAt(0) || '🚁')}
        </div>

        <h1 style={{ fontFamily: 'var(--f-display)', fontWeight: 700, color: 'var(--text-bright)', fontSize: '1.6rem', marginBottom: '0.4rem', letterSpacing: '-0.3px' }}>
          {p.userId?.name || 'צלם'}
        </h1>

        {p.droneModel && (
          <p style={{ color: 'var(--primary)', fontSize: '0.85rem', marginBottom: '0.5rem', fontFamily: 'var(--f-body)', fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
            <svg viewBox="0 0 36 36" width="16" height="16" fill="none" style={{ flexShrink: 0 }}>
              <line x1="18" y1="18" x2="6"  y2="6"  stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <line x1="18" y1="18" x2="30" y2="6"  stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <line x1="18" y1="18" x2="6"  y2="30" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <line x1="18" y1="18" x2="30" y2="30" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <circle cx="6"  cy="6"  r="4" stroke="currentColor" strokeWidth="1.5"/>
              <circle cx="30" cy="6"  r="4" stroke="currentColor" strokeWidth="1.5"/>
              <circle cx="6"  cy="30" r="4" stroke="currentColor" strokeWidth="1.5"/>
              <circle cx="30" cy="30" r="4" stroke="currentColor" strokeWidth="1.5"/>
              <rect x="13" y="13" width="10" height="10" rx="2.5" fill="currentColor"/>
            </svg>
            {p.droneModel}
          </p>
        )}

        {p.userId?.bio && (
          <p style={{ color: 'var(--text-dim)', fontSize: '0.9rem', maxWidth: 480, margin: '0 auto 1.25rem', lineHeight: 1.6 }}>{p.userId.bio}</p>
        )}

        <div className="stat-row">
          {[
            { val: p.portfolio?.length || 0,             label: 'פוסטים',       color: 'var(--text-bright)' },
            { val: p.totalJobs || 0,                     label: 'הזמנות',       color: 'var(--success)' },
            { val: p.rating?.toFixed(1) || '—',          label: 'דירוג',        color: 'var(--warn)' },
            { val: `₪${p.pricePerJob || '—'}`,           label: 'לעבודה',       color: 'var(--primary)' },
            { val: `${p.averageDeliveryTime || 30} דק'`, label: 'זמן הגעה',     color: '#a78bfa' },
          ].map(({ val, label, color }) => (
            <div key={label} className="stat-pill">
              <span className="stat-pill-val" style={{ color }}>{val}</span>
              <span className="stat-pill-label">{label}</span>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap', marginBottom: '1.5rem' }}>
          <span className={`avail-badge ${p.isAvailable ? 'available' : 'unavailable'}`}>
            ● {p.isAvailable ? 'זמין עכשיו' : 'לא זמין'}
          </span>
          {p.serviceAreas?.filter(Boolean).map(area => (
            <span key={area} className="tag-pill">{area}</span>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center' }}>
          <button className="btn btn-primary" style={{ padding: '0.8rem 2.5rem', fontSize: '0.85rem' }} onClick={() => setShowBooking(true)}>
            הזמן עכשיו
          </button>
        </div>
      </div>

      {/* ── Portfolio Grid ── */}
      <div style={{ padding: '2rem' }}>
        <h2 style={{ fontFamily: 'var(--f-display)', fontWeight: 700, color: 'var(--text-bright)', fontSize: '1rem', marginBottom: '1.25rem', letterSpacing: '-0.1px' }}>
          פורטפוליו — {p.portfolio?.length || 0} עבודות
        </h2>

        {!p.portfolio?.length ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-dim)', fontFamily: 'var(--f-body)', fontSize: '0.9rem' }}>
            עדיין אין עבודות בפורטפוליו
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '3px' }}>
            {p.portfolio.map(item => (
              <div key={item._id} onClick={() => setSelectedItem(item)}
                style={{ aspectRatio: '1', overflow: 'hidden', background: 'var(--surface)', position: 'relative', cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.querySelector('.pi-ov').style.opacity = 1}
                onMouseLeave={e => e.currentTarget.querySelector('.pi-ov').style.opacity = 0}>
                {item.type === 'video'
                  ? <video src={mediaUrl(item.url)} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <img src={mediaUrl(item.url)} alt={item.title} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />}
                <div className="pi-ov" style={{
                  position: 'absolute', inset: 0,
                  background: 'rgba(4,8,15,0.65)',
                  backdropFilter: 'blur(2px)',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
                  opacity: 0, transition: '0.2s', color: '#fff'
                }}>
                  {item.type === 'video' ? (
                    <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 32, height: 32, opacity: 0.9 }}><path fillRule="evenodd" d="M4.5 5.653c0-1.426 1.529-2.33 2.779-1.643l11.54 6.348c1.295.712 1.295 2.573 0 3.285L7.28 19.991c-1.25.687-2.779-.217-2.779-1.643V5.653z" clipRule="evenodd"/></svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ width: 28, height: 28 }}><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"/></svg>
                  )}
                  {item.title && (
                    <div style={{ fontFamily: 'var(--f-body)', fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.3px', textAlign: 'center', padding: '0 8px', maxWidth: '90%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.title}
                    </div>
                  )}
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.68rem', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 11, height: 11 }}><path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z"/></svg>
                    {item.likes}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Item Lightbox ── */}
      {selectedItem && (
        <div className="modal-overlay" onClick={() => setSelectedItem(null)}>
          <div style={{ background: 'var(--surface)', borderRadius: 'var(--r-xl)', overflow: 'hidden', maxWidth: 900, width: '95vw', boxShadow: '0 24px 80px rgba(0,0,0,0.7)', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
            {/* media */}
            <div style={{ background: '#000', position: 'relative' }}>
              {selectedItem.type === 'video'
                ? <video src={mediaUrl(selectedItem.url)} controls style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain', display: 'block' }} />
                : <img src={mediaUrl(selectedItem.url)} alt={selectedItem.title} style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain', display: 'block' }} />}
              <button onClick={() => setSelectedItem(null)} style={{ position: 'absolute', top: 12, left: 12, background: 'rgba(0,0,0,0.55)', border: 'none', color: '#fff', borderRadius: '50%', width: 34, height: 34, fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(6px)' }}>×</button>
            </div>
            {/* info */}
            <div style={{ padding: '1rem 1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
              <div>
                {selectedItem.title && <div style={{ fontFamily: 'var(--f-display)', fontWeight: 700, color: 'var(--text-bright)', fontSize: '0.95rem', marginBottom: 3 }}>{selectedItem.title}</div>}
                {selectedItem.description && <div style={{ color: 'var(--text-dim)', fontSize: '0.82rem', lineHeight: 1.5 }}>{selectedItem.description}</div>}
                {selectedItem.location?.address && (
                  <div style={{ color: 'var(--primary)', fontSize: '0.78rem', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="12" height="12"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"/></svg>
                    {selectedItem.location.address}
                  </div>
                )}
              </div>
              {user && user.role !== 'photographer' && (
                <button className="btn btn-primary" style={{ flexShrink: 0, padding: '0.65rem 1.5rem' }} onClick={() => { setSelectedItem(null); setShowBooking(true); }}>
                  הזמן את {p.userId?.name}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Booking Form Modal ── */}
      {showBooking && (
        <div className="modal-overlay" onClick={() => setShowBooking(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>הזמנת {p.userId?.name}</h3>
              <button className="modal-close" onClick={() => setShowBooking(false)}>×</button>
            </div>
            <div>
              <label style={lbl}>כתובת לצילום</label>
              <input style={inp2} type="text" value={bookingData.address} onChange={e => setBookingData(b => ({ ...b, address: e.target.value }))} placeholder="הכנס כתובת..." />
              <label style={lbl}>תאריך ושעה</label>
              <input style={inp2} type="datetime-local" value={bookingData.scheduledTime} onChange={e => setBookingData(b => ({ ...b, scheduledTime: e.target.value }))} />
              <label style={lbl}>הערות</label>
              <textarea style={{ ...inp2, resize: 'vertical' }} rows={3} value={bookingData.notes} onChange={e => setBookingData(b => ({ ...b, notes: e.target.value }))} placeholder="פרטים נוספים..." />
              <button className="btn btn-primary" style={{ width: '100%', padding: '13px', marginTop: 4 }} onClick={handleBooking}>
                שלח הזמנה
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Booking Success Modal ── */}
      {bookingResult && (
        <div className="modal-overlay" onClick={() => { setBookingResult(null); navigate('/dashboard'); }}>
          <div className="modal" style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
            <div style={{ marginBottom: '1rem' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="var(--success)" strokeWidth="1.5" style={{ width: 52, height: 52, filter: 'drop-shadow(0 0 12px rgba(16,185,129,0.4))' }}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
            </div>
            <h3 style={{ fontFamily: 'var(--f-display)', fontWeight: 800, color: 'var(--success)', marginBottom: '0.5rem', fontSize: '1.2rem', letterSpacing: '-0.2px' }}>ההזמנה נשלחה!</h3>
            <p style={{ color: 'var(--text-dim)', fontSize: '0.85rem', marginBottom: '1.5rem', lineHeight: 1.6 }}>
              לאחר התשלום תישלח הודעת WhatsApp לצלם אוטומטית.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {bookingResult.paymentUrl && (
                <a href={bookingResult.paymentUrl}
                  style={{ display: 'block', background: '#00c8f0', color: '#001520', borderRadius: 10, padding: '0.9rem', fontWeight: 700, fontSize: '0.9rem', textDecoration: 'none' }}>
                  💳 המשך לתשלום
                </a>
              )}
              <button onClick={() => { setBookingResult(null); navigate('/dashboard'); }}
                className="btn btn-outline" style={{ width: '100%' }}>
                לוח בקרה
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Dashboard
// ── Subscription Tab ──
const COMMISSION_RATE = 15;
const PLANS = [
  { id: 'monthly', label: 'חודשי',  price: 99,  period: 'לחודש',   note: 'חיוב חודשי, ניתן לביטול בכל עת' },
  { id: 'yearly',  label: 'שנתי',   price: 799, period: 'לשנה',    note: 'חסכון של כ-33% לעומת חודשי', highlight: true },
];

function SubscriptionTab({ profile, onSubscribed, showOk, showErr }) {
  const navigate = useNavigate();
  const sub = profile?.subscription;
  const isActive = sub?.active && sub?.expiresAt && new Date(sub.expiresAt) > new Date();

  const fmt = (d) => d ? new Date(d).toLocaleDateString('he-IL') : '—';

  return (
    <div style={{ maxWidth: 540 }}>
      <h3 style={{ marginBottom: 6 }}>מנוי צלם</h3>
      <p style={{ color: 'var(--text-dim)', fontSize: '0.85rem', marginBottom: '1.5rem', lineHeight: 1.7 }}>
        מנוי פעיל מאפשר להעלות תוכן לפיד, להופיע במפה ולקבל הזמנות מלקוחות.<br />
        האתר גובה <strong style={{ color: 'var(--primary)' }}>{COMMISSION_RATE}% עמלה</strong> על כל עבודה שמגיעה דרך הפלטפורמה.
      </p>

      {/* Current status */}
      {isActive && (
        <div style={{ background: 'rgba(0,232,135,0.07)', border: '1px solid rgba(0,232,135,0.2)', borderRadius: 12, padding: '1rem 1.25rem', marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div style={{ fontFamily: 'Oxanium, monospace', fontWeight: 700, color: 'var(--success)', fontSize: '0.85rem', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 3 }}>● מנוי פעיל</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>תכנית: <strong style={{ color: 'var(--text)' }}>{sub.plan === 'yearly' ? 'שנתי' : 'חודשי'}</strong> · בתוקף עד: <strong style={{ color: 'var(--text)' }}>{fmt(sub.expiresAt)}</strong></div>
          </div>
          <button onClick={() => navigate(`/checkout?plan=${sub.plan}`)} className="btn btn-outline" style={{ fontSize: '0.75rem' }}>
            חדש מנוי
          </button>
        </div>
      )}

      {/* Plan cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
        {PLANS.map(plan => (
          <div key={plan.id} style={{
            background: plan.highlight ? 'rgba(0,200,240,0.06)' : 'var(--surface)',
            border: `1px solid ${plan.highlight ? 'var(--border-2)' : 'var(--border)'}`,
            borderRadius: 16, padding: '1.5rem', textAlign: 'center', position: 'relative',
            boxShadow: plan.highlight ? 'var(--glow-sm)' : 'none'
          }}>
            {plan.highlight && (
              <div style={{ position: 'absolute', top: -11, left: '50%', transform: 'translateX(-50%)', background: 'var(--primary)', color: '#001520', fontFamily: 'Oxanium, monospace', fontWeight: 800, fontSize: '0.65rem', letterSpacing: '1px', padding: '3px 12px', borderRadius: 99, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                הכי משתלם
              </div>
            )}
            <div style={{ fontFamily: 'Oxanium, monospace', fontWeight: 800, letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--text-bright)', fontSize: '0.9rem', marginBottom: '0.75rem' }}>{plan.label}</div>
            <div style={{ fontFamily: 'Oxanium, monospace', fontWeight: 800, fontSize: '2.2rem', color: plan.highlight ? 'var(--primary)' : 'var(--text-bright)', lineHeight: 1, marginBottom: '0.25rem' }}>₪{plan.price}</div>
            <div style={{ color: 'var(--text-dim)', fontSize: '0.75rem', marginBottom: '0.75rem', fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.5px' }}>{plan.period}</div>
            <div style={{ color: 'var(--text-dim)', fontSize: '0.72rem', marginBottom: '1.25rem', lineHeight: 1.5 }}>{plan.note}</div>
            <button onClick={() => navigate(`/checkout?plan=${plan.id}`)} className={`btn ${plan.highlight ? 'btn-primary' : 'btn-outline'}`} style={{ width: '100%', padding: '0.65rem', fontSize: '0.78rem' }}>
              {isActive && sub?.plan === plan.id ? '✓ פעיל — חדש מנוי' : 'לרכישה ותשלום'}
            </button>
          </div>
        ))}
      </div>

      {/* Commission info */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '1rem 1.25rem' }}>
        <div style={{ fontFamily: 'Oxanium, monospace', fontWeight: 700, fontSize: '0.78rem', letterSpacing: '1.5px', textTransform: 'uppercase', color: 'var(--text-bright)', marginBottom: '0.6rem' }}>מודל העמלה</div>
        {[
          ['מחיר שהלקוח משלם', '₪500 (לדוגמה)'],
          [`עמלת האתר (${COMMISSION_RATE}%)`, `₪${500 * COMMISSION_RATE / 100}`],
          ['שמגיע לצלם', `₪${500 - 500 * COMMISSION_RATE / 100}`],
        ].map(([k, v]) => (
          <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', padding: '4px 0', borderBottom: '1px solid var(--border)' }}>
            <span style={{ color: 'var(--text-dim)' }}>{k}</span>
            <span style={{ color: 'var(--text)', fontFamily: 'Oxanium, monospace', fontWeight: 700 }}>{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Dashboard() {
  const { user, login, logout } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const paymentResult = searchParams.get('payment');
  const [paymentToast, setPaymentToast] = useState(paymentResult || '');

  // payment toast auto-dismiss (loadProfile called inside main useEffect below)
  useEffect(() => {
    if (!paymentToast) return;
    setSearchParams({}, { replace: true });
    const t = setTimeout(() => setPaymentToast(''), 5000);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [activeTab, setActiveTab] = useState(paymentResult === 'success' ? 'sub' : 'orders');

  // Orders
  const [orders, setOrders] = useState([]);

  // Account (all users)
  const [userInfo, setUserInfo] = useState({ name: user?.name || '', bio: '', phone: '' });
  const [savingUser, setSavingUser] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const avatarRef = useRef(null);

  // Photographer profile
  const [profile, setProfile] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [droneData, setDroneData] = useState({ droneModel: '', pricePerJob: '', serviceAreas: '', arrivalTime: '' });

  // Media management
  const [editingItem, setEditingItem] = useState(null);
  const [editingLocation, setEditingLocation] = useState(null);
  const [uploadData, setUploadData] = useState({ title: '', description: '', type: 'photo', lat: null, lng: null, address: '' });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [geocodingAddress, setGeocodingAddress] = useState(false);
  const [geocodeResults, setGeocodeResults] = useState([]);
  const geocodeSetterRef = useRef(null);

  // Feedback
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const showOk  = (m) => { setSuccessMsg(m); setTimeout(() => setSuccessMsg(''), 3000); };
  const showErr = (m) => { setErrorMsg(m);   setTimeout(() => setErrorMsg(''), 4000); };

  const inp = { width: '100%', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'var(--f-body)', background: 'var(--surface-2)', color: 'var(--text)' };

  useEffect(() => {
    loadOrders();
    api.get('/auth/me').then(r => setUserInfo({ name: r.data.name || '', bio: r.data.bio || '', phone: r.data.phone || '' })).catch(() => {});
    if (user?.role === 'photographer') loadProfile();
  }, [user]);

  const loadOrders = async () => {
    try { const r = await api.get('/orders'); setOrders(r.data); } catch {}
  };

  const loadProfile = async () => {
    try {
      const r = await api.get('/photographers');
      const mine = r.data.find(p => p.userId?._id === user?.id);
      setProfile(mine);
      if (mine) setDroneData({ droneModel: mine.droneModel || '', pricePerJob: mine.pricePerJob || '', serviceAreas: mine.serviceAreas?.join(', ') || '', arrivalTime: mine.averageDeliveryTime || '' });
    } catch {}
  };

  // ── Account ──
  const saveUserInfo = async () => {
    setSavingUser(true);
    try {
      const r = await api.put('/users/profile', userInfo);
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      const updated = { ...stored, name: r.data.name, bio: r.data.bio, phone: r.data.phone };
      localStorage.setItem('user', JSON.stringify(updated));
      login(localStorage.getItem('token'), updated);
      showOk('נשמר!');
    } catch { showErr('שגיאה בשמירה'); }
    finally { setSavingUser(false); }
  };

  const deleteAccount = async () => {
    setDeletingAccount(true);
    try {
      await api.delete('/users/me');
      logout();
    } catch { showErr('שגיאה במחיקת החשבון'); setDeletingAccount(false); setShowDeleteConfirm(false); }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const form = new FormData();
    form.append('image', file);
    try {
      const r = await api.post('/users/profile-image', form);
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      const updated = { ...stored, profileImage: r.data.profileImage };
      localStorage.setItem('user', JSON.stringify(updated));
      login(localStorage.getItem('token'), updated);
      showOk('תמונה עודכנה!');
    } catch { showErr('שגיאה בהעלאת התמונה'); }
  };

  // ── Drone profile ──
  const saveDroneProfile = async () => {
    try {
      await api.put('/photographers/profile', { droneModel: droneData.droneModel, pricePerJob: parseInt(droneData.pricePerJob), serviceAreas: droneData.serviceAreas.split(',').map(s => s.trim()), averageDeliveryTime: parseInt(droneData.arrivalTime) || 30 });
      showOk('פרטי הצלם עודכנו!');
      setEditMode(false);
      loadProfile();
    } catch { showErr('שגיאה בשמירה'); }
  };

  const updateAvailability = async (v) => { try { await api.put('/photographers/profile', { isAvailable: v }); loadProfile(); } catch {} };
  const updateLocation = () => {
    navigator.geolocation?.getCurrentPosition(async pos => {
      try { await api.put('/photographers/location', { lat: pos.coords.latitude, lng: pos.coords.longitude }); showOk('מיקום עודכן!'); } catch {}
    });
  };

  // ── Orders ──
  const updateStatus = async (id, status) => {
    try {
      await api.put(`/orders/${id}/status`, { status });
      await loadOrders();
    } catch {}
  };

  const markPaid = async (id) => {
    try {
      await api.put(`/orders/${id}/mark-paid`);
      await loadOrders();
      showOk('סומן כשולם ✓');
    } catch { showErr('שגיאה'); }
  };

  // ── Media ──
  const detectLocation = () => {
    if (!navigator.geolocation) return showErr('הדפדפן לא תומך ב-GPS');
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(async pos => {
      const { latitude: lat, longitude: lng } = pos.coords;
      let address = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
      try { const r = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`); const d = await r.json(); address = d.display_name?.split(',').slice(0, 3).join(', ') || address; } catch {}
      setUploadData(u => ({ ...u, lat, lng, address }));
      setGettingLocation(false);
    }, () => { showErr('לא ניתן לקבל מיקום'); setGettingLocation(false); });
  };

  const detectLocationForEdit = () => {
    if (!navigator.geolocation) return showErr('הדפדפן לא תומך ב-GPS');
    navigator.geolocation.getCurrentPosition(async pos => {
      const { latitude: lat, longitude: lng } = pos.coords;
      let address = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
      try { const r = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`); const d = await r.json(); address = d.display_name?.split(',').slice(0, 3).join(', ') || address; } catch {}
      setEditingLocation(l => ({ ...l, lat, lng, address }));
    }, () => showErr('לא ניתן לקבל מיקום'));
  };

  const geocodeAddress = async (addressText, setter) => {
    if (!addressText?.trim()) return;
    setGeocodingAddress(true);
    geocodeSetterRef.current = setter;
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(addressText)}&format=json&limit=5&countrycodes=il&addressdetails=1`);
      const d = await r.json();
      if (!d.length) { showErr('הכתובת לא נמצאה, נסה ניסוח אחר'); setGeocodeResults([]); }
      else if (d.length === 1) { const { lat, lon, display_name } = d[0]; setter(parseFloat(lat), parseFloat(lon), display_name.split(',').slice(0, 4).join(', ')); setGeocodeResults([]); }
      else { setGeocodeResults(d); }
    } catch { showErr('שגיאה בחיפוש כתובת'); }
    finally { setGeocodingAddress(false); }
  };

  const pickGeoResult = (result) => {
    const { lat, lon, display_name } = result;
    geocodeSetterRef.current?.(parseFloat(lat), parseFloat(lon), display_name.split(',').slice(0, 4).join(', '));
    setGeocodeResults([]);
  };

  const saveLocation = async () => {
    if (!editingLocation?.lat || !editingLocation?.lng) { showErr('נדרש מיקום תקין'); return; }
    try {
      await api.put(`/photographers/portfolio/${editingLocation.id}/location`, { lat: editingLocation.lat, lng: editingLocation.lng, address: editingLocation.address });
      setEditingLocation(null);
      await loadProfile();
      showOk('מיקום עודכן!');
    } catch { showErr('שגיאה בשמירת מיקום'); }
  };

  /**
   * Compress video client-side using canvas + MediaRecorder
   * Reduces resolution and bitrate to fit under target size
   */
  const compressVideoClientSide = async (file, targetMB = 95) => {
    const targetBytes = targetMB * 1024 * 1024;
    const originalSize = file.size;
    
    showOk('🎬 דוחס וידאו בדפדפן...');
    setUploadProgress(0);

    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.muted = true;
      video.src = URL.createObjectURL(file);
      
      video.onloadedmetadata = async () => {
        const duration = video.duration;
        const originalWidth = video.videoWidth;
        const originalHeight = video.videoHeight;
        
        // Calculate target resolution based on target size
        let targetWidth, targetHeight;
        if (originalHeight > 720) {
          targetWidth = 720;
          targetHeight = Math.round(originalHeight * (720 / originalWidth));
          if (targetHeight % 2 !== 0) targetHeight -= 1;
        } else {
          targetWidth = originalWidth;
          targetHeight = originalHeight;
        }
        if (targetWidth % 2 !== 0) targetWidth -= 1;

        // Calculate target bitrate
        const targetBitrate = Math.floor((targetBytes * 8) / duration) - 128000; // -128kbps for audio
        const minBitrate = 200000; // minimum 200kbps video
        const finalBitrate = Math.max(targetBitrate, minBitrate);

        console.log(`[compress] ${originalSize/1024/1024}MB → target ${targetMB}MB`);
        console.log(`[compress] ${originalWidth}x${originalHeight} → ${targetWidth}x${targetHeight}`);
        console.log(`[compress] bitrate: ${(finalBitrate/1000).toFixed(0)}kbps`);

        // Create canvas for re-encoding
        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext('2d');

        // Get video audio track
        let audioCtx, audioSource, audioStream = null;
        try {
          const response = await fetch(URL.createObjectURL(file));
          const audioCtxTemp = new (window.AudioContext || window.webkitAudioContext)();
          const audioSourceTemp = await audioCtxTemp.createMediaElementSource(video);
          // Note: We'll record without audio for simplicity, or use the video's audio track
        } catch (e) {
          console.log('[compress] Audio capture skipped');
        }

        // Create canvas stream at target bitrate
        const canvasStream = canvas.captureStream(30); // 30fps
        
        // Combine with video track if available
        const videoTracks = video.captureStream?.().getVideoTracks();
        if (videoTracks && videoTracks.length > 0) {
          // Use MediaRecorder on canvas
          const combinedStream = new MediaStream([
            ...canvasStream.getTracks(),
          ]);
          
          // Determine mime type
          const mimeTypes = [
            'video/webm;codecs=vp9,opus',
            'video/webm;codecs=vp8,opus',
            'video/webm;codecs=h264',
            'video/webm',
            'video/mp4'
          ];
          let mimeType = '';
          for (const mt of mimeTypes) {
            if (MediaRecorder.isTypeSupported(mt)) {
              mimeType = mt;
              break;
            }
          }
          
          if (!mimeType) {
            URL.revokeObjectURL(video.src);
            return reject(new Error('דפדפן לא תומך בהקלטת וידאו'));
          }

          const recorder = new MediaRecorder(combinedStream, {
            mimeType,
            videoBitsPerSecond: finalBitrate
          });

          const chunks = [];
          recorder.ondataavailable = (e) => {
            if (e.data.size > 0) chunks.push(e.data);
          };

          recorder.onstop = () => {
            const blob = new Blob(chunks, { type: mimeType || 'video/webm' });
            const compressedFile = new File([blob], file.name.replace(/\.[^.]+$/, '.webm'), {
              type: mimeType || 'video/webm'
            });
            
            console.log(`[compress] Result: ${(compressedFile.size/1024/1024).toFixed(1)}MB (${((1 - compressedFile.size/originalSize)*100).toFixed(0)}% reduction)`);
            
            URL.revokeObjectURL(video.src);
            resolve(compressedFile);
          };

          recorder.start(1000); // Collect data every second
          
          // Draw video frames to canvas
          const drawFrame = () => {
            if (video.paused || video.ended) return;
            ctx.drawImage(video, 0, 0, targetWidth, targetHeight);
            requestAnimationFrame(drawFrame);
          };
          drawFrame();

          // Play video to trigger frame rendering
          video.play();
          
          // Stop recording when video ends
          video.ontimeupdate = () => {
            setUploadProgress(Math.min(95, Math.round((video.currentTime / duration) * 95)));
          };
          
          video.onended = () => {
            recorder.stop();
            video.pause();
          };

          // Timeout safety net
          setTimeout(() => {
            if (recorder.state === 'recording') {
              recorder.stop();
              video.pause();
            }
          }, (duration + 10) * 1000);
        } else {
          URL.revokeObjectURL(video.src);
          reject(new Error('לא ניתן לקרוא את הוידאו'));
        }
      };

      video.onerror = () => {
        URL.revokeObjectURL(video.src);
        reject(new Error('שגיאה בטעינת הוידאו'));
      };

      video.load();
    });
  };

  const uploadMedia = async (e) => {
    e.preventDefault();
    if (!uploadFile) return;
    if (!uploadData.lat || !uploadData.lng) {
      showErr('נדרש מיקום הצילום — לחץ על GPS או הכנס כתובת');
      return;
    }
    if (uploadFile.size > 200 * 1024 * 1024) {
      showErr('הקובץ גדול מדי — מקסימום 200MB לסרטון');
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    try {
      // Step 1: get presigned URL (tiny request through tunnel)
      const { data: { signedUrl, publicUrl } } = await api.post('/upload/r2-signed-url', {
        filename: uploadFile.name,
        contentType: uploadFile.type || 'application/octet-stream',
      });

      // Step 2: PUT directly to R2 — bypasses Cloudflare tunnel, no size limit
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', signedUrl);
        xhr.setRequestHeader('Content-Type', uploadFile.type || 'application/octet-stream');
        xhr.upload.onprogress = ev => {
          if (ev.lengthComputable) setUploadProgress(Math.round((ev.loaded / ev.total) * 100));
        };
        xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`שגיאת אחסון: ${xhr.status}`));
        xhr.onerror = () => reject(new Error('שגיאת רשת — נסה שנית'));
        xhr.send(uploadFile);
      });

      await api.post('/photographers/portfolio/save', {
        url: publicUrl,
        type: uploadData.type,
        title: uploadData.title,
        description: uploadData.description,
        lat: uploadData.lat,
        lng: uploadData.lng,
        address: uploadData.address,
      });

      setUploadFile(null);
      setUploadData({ title: '', description: '', type: 'photo', lat: null, lng: null, address: '' });
      setUploadProgress(0);
      await loadProfile();
      setActiveTab('media');
      showOk('הועלה בהצלחה!');
    } catch (err) {
      const msg = err.response?.data?.msg || err.response?.data?.error || err.message || 'שגיאה בהעלאה';
      showErr(msg);
      console.error('Upload error:', err.response?.data || err.message);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('למחוק?')) return;
    try { await api.delete(`/photographers/portfolio/${id}`); await loadProfile(); showOk('נמחק'); } catch { showErr('שגיאה במחיקה'); }
  };

  const saveItemEdit = async () => {
    try { await api.put(`/photographers/portfolio/${editingItem.id}`, { title: editingItem.title, description: editingItem.description }); setEditingItem(null); await loadProfile(); showOk('נשמר!'); } catch { showErr('שגיאה'); }
  };

  const avatarSrc = user?.profileImage ? (mediaUrl(user.profileImage)) : null;

  const IcoOrders = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z"/></svg>;
  const IcoUser = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"/></svg>;
  const IcoCreditCard = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z"/></svg>;
  const IcoDrone = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5"/></svg>;
  const IcoPhoto = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"/></svg>;
  const IcoUpload = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"/></svg>;

  const tabs = [
    { id: 'orders',  label: 'הזמנות',      icon: <IcoOrders /> },
    { id: 'account', label: 'החשבון שלי',  icon: <IcoUser /> },
    ...(user?.role === 'photographer' ? [
      { id: 'sub',     label: 'מנוי',         icon: <IcoCreditCard /> },
      { id: 'drone',   label: 'פרטי צלם',    icon: <IcoDrone /> },
      { id: 'media',   label: 'הפוסטים שלי', icon: <IcoPhoto /> },
      { id: 'upload',  label: 'העלאה',        icon: <IcoUpload /> },
    ] : []),
  ];

  return (
    <div className="dashboard">
      <div className="sidebar">
        {tabs.map(t => (
          <button key={t.id} className={`sidebar-tab${activeTab === t.id ? ' active' : ''}`} onClick={() => setActiveTab(t.id)}>
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>

      <div className="dashboard-content">
        <div className="dash-welcome">
          <div>
            <div className="dash-welcome-name">שלום, {user?.name}</div>
            <div className="dash-welcome-role">{user?.role === 'photographer' ? 'צלם רחפנים מקצועי' : 'לקוח'}</div>
          </div>
        </div>

        {paymentToast === 'success' && <div className="alert alert-success">✅ התשלום בוצע בהצלחה! המנוי הופעל.</div>}
        {paymentToast === 'failed'  && <div className="alert alert-error">❌ התשלום נכשל או בוטל. ניתן לנסות שוב.</div>}
        {paymentToast === 'error'   && <div className="alert alert-error">❌ שגיאה בעיבוד התשלום. פנה לתמיכה.</div>}
        {successMsg && <div className="alert alert-success">{successMsg}</div>}
        {errorMsg   && <div className="alert alert-error">{errorMsg}</div>}

        {/* ── הזמנות ── */}
        {activeTab === 'orders' && (
          <div>
            <div className="dash-stats-row">
              {[
                { val: orders.length, label: 'סה"כ הזמנות' },
                { val: orders.filter(o => o.status === 'pending').length, label: 'ממתינות' },
                { val: orders.filter(o => o.status === 'completed' || o.status === 'delivered').length, label: 'הושלמו' },
                { val: orders.filter(o => o.paymentStatus === 'paid').length, label: 'שולמו' },
              ].map(({ val, label }) => (
                <div key={label} className="dash-stat-card">
                  <span className="dash-stat-card-val">{val}</span>
                  <span className="dash-stat-card-label">{label}</span>
                </div>
              ))}
            </div>
            {orders.length === 0
              ? <p style={{ color: 'var(--text-dim)', padding: '1rem 0' }}>אין הזמנות עדיין</p>
              : orders.map(order => (
              <div key={order._id} className="order-card-v2">
                <div className="order-card-top">
                  <div className="order-card-id">הזמנה #{order._id.slice(-6)}</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span className={`order-status ${order.status}`}>
                      {order.status === 'pending' ? 'ממתין' : order.status === 'accepted' ? 'התקבל' : order.status === 'in_progress' ? 'בתהליך' : order.status === 'completed' ? 'הושלם' : order.status === 'delivered' ? 'נמסר' : 'בוטל'}
                    </span>
                    {order.paymentStatus === 'pending_payment' && order.status !== 'cancelled' && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span className="pay-badge pending-payment">⏳ ממתין לתשלום</span>
                        {user?.role === 'photographer' && (
                          <button onClick={() => markPaid(order._id)} className="btn btn-outline" style={{ fontSize: 11, padding: '2px 10px', borderRadius: 20, borderColor: 'rgba(34,197,94,0.4)', color: '#16a34a' }}>✓ סמן כשולם</button>
                        )}
                      </span>
                    )}
                    {order.paymentStatus === 'paid' && (
                      <span className="pay-badge paid">✓ שולם</span>
                    )}
                  </div>
                </div>
                <div className="order-meta">
                  <div className="order-meta-row">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"/></svg>
                    {order.location?.address}
                  </div>
                  {order.scheduledTime && (
                    <div className="order-meta-row">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 9v7.5"/></svg>
                      {new Date(order.scheduledTime).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  )}
                  <div className="order-meta-row">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                    ₪{order.price}{order.commission ? ` | עמלה: ₪${order.commission}` : ''}
                  </div>
                </div>
                {user?.role === 'photographer' && order.clientId && (
                  <div className="order-client-box">
                    <p>{order.clientId.name}</p>
                    {order.clientId.phone && <p>📞 {order.clientId.phone}</p>}
                    {order.clientId.email && <p>✉️ {order.clientId.email}</p>}
                  </div>
                )}
                {order.notes && <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--text-dim)', fontStyle: 'italic' }}>"{order.notes}"</p>}
                <div className="order-actions">
                  {user?.role === 'photographer' && order.status === 'pending' && (
                    <>
                      <button className="btn btn-primary" onClick={() => updateStatus(order._id, 'accepted')}>קבל</button>
                      <button className="btn btn-danger" onClick={() => updateStatus(order._id, 'cancelled')}>דחה</button>
                    </>
                  )}
                  {user?.role === 'photographer' && order.status === 'accepted' && <button className="btn btn-primary" onClick={() => updateStatus(order._id, 'in_progress')}>התחל צילום</button>}
                  {user?.role === 'photographer' && order.status === 'in_progress' && <button className="btn btn-secondary" onClick={() => updateStatus(order._id, 'completed')}>סיים צילום</button>}
                  {order.status === 'delivered' && order.deliveredVideoUrl && <a href={order.deliveredVideoUrl} className="btn btn-primary" download>הורד וידאו</a>}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── החשבון שלי ── */}
        {activeTab === 'account' && (
          <div style={{ maxWidth: 480 }}>
            {/* Avatar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
              <div onClick={() => avatarRef.current?.click()} style={{ width: 80, height: 80, borderRadius: '50%', cursor: 'pointer', flexShrink: 0, background: avatarSrc ? 'transparent' : 'linear-gradient(135deg,var(--primary),#0044cc)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, color: '#fff', fontWeight: 700, overflow: 'hidden', border: '2px solid var(--border-2)', boxShadow: 'var(--glow-sm)' }}>
                {avatarSrc ? <img src={avatarSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : user?.name?.charAt(0)?.toUpperCase()}
              </div>
              <input ref={avatarRef} type="file" accept="image/*" onChange={uploadAvatar} style={{ display: 'none' }} />
              <button onClick={() => avatarRef.current?.click()} style={{ background: 'none', border: 'none', color: 'var(--primary)', fontWeight: 600, cursor: 'pointer', fontSize: 13, fontFamily: 'var(--f-body)' }}>שנה תמונת פרופיל</button>
            </div>
            {/* Fields */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {[['שם מלא','name','text','השם שלך'],['ביוגרפיה','bio','textarea','ספר על עצמך...'],['טלפון','phone','tel','050-0000000']].map(([label, key, type, ph]) => (
                <div key={key}>
                  <label className="dash-label">{label}</label>
                  {type === 'textarea'
                    ? <textarea value={userInfo[key]} rows={3} placeholder={ph} onChange={e => setUserInfo(u => ({ ...u, [key]: e.target.value }))} style={{ ...inp, resize: 'vertical' }} />
                    : <input type={type} value={userInfo[key]} placeholder={ph} onChange={e => setUserInfo(u => ({ ...u, [key]: e.target.value }))} style={inp} />}
                </div>
              ))}
              <button onClick={saveUserInfo} disabled={savingUser} className="btn btn-primary" style={{ width: '100%', padding: '13px', opacity: savingUser ? 0.5 : 1 }}>
                {savingUser ? 'שומר...' : 'שמור שינויים'}
              </button>
            </div>

            {/* Danger zone */}
            <div className="danger-zone">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div className="danger-zone-title">אזור מסוכן</div>
                  <div className="danger-zone-desc">מחיקת החשבון היא פעולה בלתי הפיכה</div>
                </div>
                <button onClick={() => setShowDeleteConfirm(true)} className="btn btn-danger" style={{ fontSize: '0.75rem', padding: '0.5rem 1rem', flexShrink: 0 }}>
                  מחק חשבון
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Delete Account Confirmation Modal ── */}
        {showDeleteConfirm && (
          <div className="modal-overlay" onClick={() => !deletingAccount && setShowDeleteConfirm(false)}>
            <div className="modal" style={{ maxWidth: 420, textAlign: 'center' }} onClick={e => e.stopPropagation()}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem', filter: 'drop-shadow(0 0 16px rgba(255,59,92,0.5))' }}>⚠️</div>
              <h3 style={{ fontFamily: 'Oxanium, monospace', fontWeight: 800, letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--danger)', marginBottom: '0.75rem', fontSize: '1.1rem' }}>
                מחיקת חשבון
              </h3>
              <p style={{ color: 'var(--text-dim)', fontSize: '0.9rem', lineHeight: 1.7, marginBottom: '0.5rem' }}>
                האם אתה בטוח שברצונך למחוק את החשבון?
              </p>
              <p style={{ color: 'var(--danger)', fontSize: '0.8rem', marginBottom: '1.75rem', fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.5px' }}>
                כל הנתונים, הפוסטים וההזמנות יימחקו לצמיתות — לא ניתן לבטל פעולה זו.
              </p>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deletingAccount}
                  className="btn btn-outline"
                  style={{ flex: 1, padding: '0.8rem' }}>
                  ביטול
                </button>
                <button
                  onClick={deleteAccount}
                  disabled={deletingAccount}
                  className="btn btn-danger"
                  style={{ flex: 1, padding: '0.8rem', opacity: deletingAccount ? 0.6 : 1 }}>
                  {deletingAccount ? 'מוחק...' : 'כן, מחק לצמיתות'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── פרטי צלם ── */}
        {activeTab === 'drone' && user?.role === 'photographer' && profile && (
          <div style={{ maxWidth: 480 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3>פרטי צלם</h3>
              <button className="btn btn-outline" onClick={() => setEditMode(!editMode)}>{editMode ? 'ביטול' : '✏️ ערוך'}</button>
            </div>
            {editMode ? (
              <div className="card">
                {[
                  ['כלי טיס (רחפן)', 'droneModel', 'text', 'DJI Mavic 3'],
                  ['מחיר לעבודה (₪)', 'pricePerJob', 'number', '500'],
                  ['זמן הגעה (דקות)', 'arrivalTime', 'number', '30'],
                  ['איזורי שירות (פסיק)', 'serviceAreas', 'text', 'תל אביב, חיפה'],
                ].map(([label, key, type, ph]) => (
                  <div className="form-group" key={key}>
                    <label>{label}</label>
                    <input type={type} value={droneData[key]} placeholder={ph} onChange={e => setDroneData(d => ({ ...d, [key]: e.target.value }))} />
                  </div>
                ))}
                <button className="btn btn-primary" onClick={saveDroneProfile}>שמור</button>
              </div>
            ) : (
              <div className="card">
                <p><strong>דירוג:</strong> {profile.rating} ⭐</p>
                <p><strong>הזמנות שהושלמו:</strong> {profile.totalJobs}</p>
                <p><strong>פוסטים שהועלו:</strong> {profile.portfolio?.length || 0}</p>
                <p><strong>מחיר לעבודה:</strong> ₪{profile.pricePerJob || 'לא הוגדר'}</p>
                <p><strong>זמן הגעה ללקוח:</strong> {profile.averageDeliveryTime || 30} דקות</p>
                <p><strong>כלי טיס:</strong> {profile.droneModel || 'לא צוין'}</p>
                <p><strong>איזורי שירות:</strong> {profile.serviceAreas?.join(', ') || 'לא הוגדרו'}</p>
                <div style={{ marginTop: '1rem', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className={`btn ${profile.isAvailable ? 'btn-secondary' : 'btn-outline'}`} onClick={() => updateAvailability(!profile.isAvailable)}>
                    {profile.isAvailable ? '🟢 זמין כרגע' : '🔴 לא זמין'}
                  </button>
                  <button className="btn btn-primary" onClick={updateLocation}>📍 עדכן מיקום</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── הפוסטים שלי ── */}
        {activeTab === 'media' && user?.role === 'photographer' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3>הפוסטים שלי <span style={{ fontWeight: 400, fontSize: 14, color: '#8e8e8e' }}>({profile?.portfolio?.length || 0})</span></h3>
              <button onClick={() => setActiveTab('upload')} style={{ background: '#0095f6', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>+ העלאה חדשה</button>
            </div>
            {!profile?.portfolio?.length ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#8e8e8e' }}><div style={{ fontSize: 48 }}>🖼️</div><p>עדיין לא העלית תוכן</p></div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8 }}>
                {profile.portfolio.map(item => (
                  <div key={item._id} style={{ position: 'relative', aspectRatio: '1', overflow: 'hidden', background: '#111', borderRadius: 8 }}
                    onMouseEnter={e => e.currentTarget.querySelector('.ov').style.opacity = 1}
                    onMouseLeave={e => e.currentTarget.querySelector('.ov').style.opacity = 0}>
                    {item.type === 'video'
                      ? <video src={mediaUrl(item.url)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <img src={mediaUrl(item.url)} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                    {item.location?.address
                      ? <div style={{ position: 'absolute', bottom: 4, right: 4, background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 10, padding: '2px 5px', borderRadius: 4 }}>📍 {item.location.address.split(',')[0]}</div>
                      : <div style={{ position: 'absolute', top: 4, left: 4, background: 'rgba(239,68,68,0.85)', color: '#fff', fontSize: 10, padding: '2px 5px', borderRadius: 4, fontWeight: 600 }}>⚠️ חסר מיקום</div>}
                    <div className="ov" style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, opacity: 0, transition: '.2s' }}>
                      <span style={{ color: '#fff', fontSize: 12 }}>❤️ {item.likes}</span>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => setEditingItem({ id: item._id, title: item.title || '', description: item.description || '' })} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 12 }}>✏️ ערוך</button>
                        <button onClick={() => setEditingLocation({ id: item._id, lat: item.location?.lat || null, lng: item.location?.lng || null, address: item.location?.address || '' })} style={{ background: item.location?.lat ? 'rgba(0,200,100,0.7)' : 'rgba(239,68,68,0.7)', border: 'none', color: '#fff', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 12 }}>📍</button>
                        <button onClick={() => deleteItem(item._id)} style={{ background: 'rgba(239,68,68,0.85)', border: 'none', color: '#fff', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 12 }}>🗑️</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── מנוי ── */}
        {activeTab === 'sub' && user?.role === 'photographer' && (
          <SubscriptionTab profile={profile} onSubscribed={loadProfile} showOk={showOk} showErr={showErr} />
        )}

        {/* ── העלאה ── */}
        {activeTab === 'upload' && user?.role === 'photographer' && (
          <div style={{ maxWidth: 480 }}>
            {/* paywall if no active subscription */}
            {(!profile?.subscription?.active || new Date(profile?.subscription?.expiresAt) < new Date()) ? (
              <div style={{ textAlign: 'center', padding: '3rem 2rem', border: '1px solid rgba(255,59,92,0.2)', borderRadius: 18, background: 'rgba(255,59,92,0.04)' }}>
                <div style={{ fontSize: '3.5rem', marginBottom: '1rem', filter: 'drop-shadow(0 0 16px rgba(255,59,92,0.4))' }}>🔒</div>
                <h3 style={{ fontFamily: 'Oxanium, monospace', fontWeight: 800, letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--danger)', marginBottom: '0.75rem', fontSize: '1rem' }}>נדרש מנוי פעיל</h3>
                <p style={{ color: 'var(--text-dim)', fontSize: '0.9rem', lineHeight: 1.7, marginBottom: '1.5rem' }}>
                  כדי להעלות תוכן לפיד ולהופיע במפה, צלמים חייבים מנוי פעיל.
                </p>
                <button className="btn btn-primary" onClick={() => setActiveTab('sub')} style={{ padding: '0.8rem 2rem' }}>
                  לרכישת מנוי
                </button>
              </div>
            ) : (
            <>
              <h3 style={{ marginBottom: 20 }}>העלאת תוכן לפיד</h3>
              <form onSubmit={uploadMedia} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div onClick={() => document.getElementById('db-file-input')?.click()} className="drop-zone" style={{ background: uploadFile ? 'var(--primary-dim)' : undefined }}>
                {uploadFile ? (
                  <><div style={{ fontSize: 36 }}>{uploadData.type === 'video' ? '🎬' : '🖼️'}</div><p style={{ fontWeight: 600, margin: '8px 0 0', color: 'var(--text-bright)' }}>{uploadFile.name}</p><p style={{ fontSize: 12, color: 'var(--text-dim)', margin: '4px 0 0' }}>{(uploadFile.size / 1024 / 1024).toFixed(1)} MB</p></>
                ) : (
                  <><div style={{ fontSize: 44, filter: 'drop-shadow(0 0 12px rgba(0,200,240,0.3))' }}>☁️</div><p style={{ fontWeight: 700, margin: '8px 0 0', color: 'var(--text)', fontSize: 13 }}>לחץ לבחירת קובץ</p><p style={{ fontSize: 12, color: 'var(--text-dim)', margin: '4px 0 0' }}>תמונות ווידאו</p></>
                )}
              </div>
              <input id="db-file-input" type="file" accept="image/*,video/*" style={{ display: 'none' }}
                onChange={e => { const f = e.target.files[0]; if (!f) return; setUploadFile(f); setUploadData(d => ({ ...d, type: f.type.startsWith('video') ? 'video' : 'photo' })); }} />
              <div>
                <label className="dash-label">כותרת</label>
                <input value={uploadData.title} onChange={e => setUploadData(d => ({ ...d, title: e.target.value }))} placeholder="כותרת הפוסט" required style={inp} />
              </div>
              <div>
                <label className="dash-label">תיאור</label>
                <textarea value={uploadData.description} onChange={e => setUploadData(d => ({ ...d, description: e.target.value }))} placeholder="תיאור..." rows={3} style={{ ...inp, resize: 'vertical' }} />
              </div>
              <div>
                <label className="dash-label">מיקום הצילום</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input value={uploadData.address} onChange={e => setUploadData(d => ({ ...d, address: e.target.value, lat: null, lng: null }))}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), geocodeAddress(uploadData.address, (lat, lng, addr) => setUploadData(u => ({ ...u, lat, lng, address: addr }))))}
                    placeholder="למשל: הרצל 5 תל אביב, או: בית שאן" style={{ ...inp, flex: 1 }} />
                  <button type="button" onClick={() => geocodeAddress(uploadData.address, (lat, lng, addr) => setUploadData(u => ({ ...u, lat, lng, address: addr })))} disabled={geocodingAddress} className="btn btn-primary" style={{ padding: '0 12px', flexShrink: 0, background: '#22c55e', borderColor: '#22c55e' }}>
                    {geocodingAddress ? '...' : '🔍'}
                  </button>
                  <button type="button" onClick={detectLocation} disabled={gettingLocation} className="btn btn-primary" style={{ padding: '0 12px', flexShrink: 0 }}>
                    {gettingLocation ? '...' : '📍'}
                  </button>
                </div>
                {geocodeResults.length > 0 && (
                  <div className="geo-results">
                    {geocodeResults.map((r, i) => (
                      <div key={i} className="geo-result-item" onClick={() => { pickGeoResult(r); setUploadData(u => ({ ...u, lat: parseFloat(r.lat), lng: parseFloat(r.lon), address: r.display_name.split(',').slice(0,4).join(', ') })); }}>
                        📍 {r.display_name.split(',').slice(0, 4).join(', ')}
                      </div>
                    ))}
                    <div className="geo-result-item" style={{ color: 'var(--text-dim)', textAlign: 'center', borderTop: '1px solid var(--border)' }} onClick={() => setGeocodeResults([])}>ביטול</div>
                  </div>
                )}
                {uploadData.lat && !geocodeResults.length && <div style={{ fontSize: 12, color: 'var(--primary)', marginTop: 6 }}>✓ {uploadData.address}</div>}
              </div>
              {uploading && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 8, color: 'var(--text-dim)' }}>
                    <span>מעלה...</span><span>{uploadProgress}%</span>
                  </div>
                  <div style={{ background: 'var(--surface-3)', borderRadius: 99, height: 6, overflow: 'hidden' }}>
                    <div style={{ background: 'linear-gradient(90deg, var(--primary), #0044cc)', height: '100%', width: `${uploadProgress}%`, transition: 'width 0.3s', borderRadius: 99, boxShadow: '0 0 8px rgba(0,200,240,0.5)' }} />
                  </div>
                </div>
              )}
              <button type="submit" disabled={uploading || !uploadFile} className="btn btn-primary" style={{ width: '100%', padding: '13px', opacity: uploading || !uploadFile ? 0.45 : 1, fontSize: '0.85rem' }}>
                {uploading ? `מעלה... ${uploadProgress}%` : 'העלה לפיד'}
              </button>
              </form>
            </>
            )}
          </div>
        )}

        {/* ── Edit Location Modal ── */}
        {editingLocation && (
          <div className="modal-overlay" onClick={() => setEditingLocation(null)}>
            <div className="modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>📍 עדכן מיקום הצילום</h3>
                <button className="modal-close" onClick={() => setEditingLocation(null)}>×</button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label className="dash-label">כתובת</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input value={editingLocation.address} onChange={e => setEditingLocation(l => ({ ...l, address: e.target.value, lat: null, lng: null }))}
                      onKeyDown={e => e.key === 'Enter' && geocodeAddress(editingLocation.address, (lat, lng, addr) => setEditingLocation(l => ({ ...l, lat, lng, address: addr })))}
                      placeholder="למשל: הרצל 5 תל אביב, או: בית שאן" style={{ ...inp, flex: 1 }} />
                    <button type="button" onClick={() => geocodeAddress(editingLocation.address, (lat, lng, addr) => setEditingLocation(l => ({ ...l, lat, lng, address: addr })))} disabled={geocodingAddress} className="btn btn-primary" style={{ padding: '0 10px', flexShrink: 0, background: '#22c55e', borderColor: '#22c55e' }}>
                      {geocodingAddress ? '...' : '🔍'}
                    </button>
                    <button type="button" onClick={detectLocationForEdit} className="btn btn-primary" style={{ padding: '0 10px', flexShrink: 0 }}>📍</button>
                  </div>
                </div>
                {geocodeResults.length > 0 && (
                  <div className="geo-results">
                    {geocodeResults.map((r, i) => (
                      <div key={i} className="geo-result-item" onClick={() => { pickGeoResult(r); setEditingLocation(l => ({ ...l, lat: parseFloat(r.lat), lng: parseFloat(r.lon), address: r.display_name.split(',').slice(0,4).join(', ') })); }}>
                        📍 {r.display_name.split(',').slice(0, 4).join(', ')}
                      </div>
                    ))}
                    <div className="geo-result-item" style={{ color: 'var(--text-dim)', textAlign: 'center', borderTop: '1px solid var(--border)' }} onClick={() => setGeocodeResults([])}>ביטול</div>
                  </div>
                )}
                {editingLocation.lat && !geocodeResults.length && (
                  <div style={{ fontSize: 12, color: 'var(--primary)' }}>✓ {editingLocation.lat.toFixed(5)}, {editingLocation.lng.toFixed(5)}</div>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={saveLocation} className="btn btn-primary" style={{ flex: 1, padding: '12px' }}>שמור מיקום</button>
                  <button onClick={() => setEditingLocation(null)} className="btn btn-outline" style={{ flex: 1, padding: '12px' }}>ביטול</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Edit Modal ── */}
        {editingItem && (
          <div className="modal-overlay" onClick={() => setEditingItem(null)}>
            <div className="modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>ערוך פוסט</h3>
                <button className="modal-close" onClick={() => setEditingItem(null)}>×</button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div><label className="dash-label">כותרת</label><input value={editingItem.title} onChange={e => setEditingItem(i => ({ ...i, title: e.target.value }))} style={inp} /></div>
                <div><label className="dash-label">תיאור</label><textarea value={editingItem.description} onChange={e => setEditingItem(i => ({ ...i, description: e.target.value }))} rows={3} style={{ ...inp, resize: 'vertical' }} /></div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={saveItemEdit} className="btn btn-primary" style={{ flex: 1, padding: '12px' }}>שמור</button>
                  <button onClick={() => setEditingItem(null)} className="btn btn-outline" style={{ flex: 1, padding: '12px' }}>ביטול</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============ CHECKOUT PAGE ============

const VAT_RATE = 0.17;
const PLAN_INFO = {
  monthly: { label: 'חודשי', gross: 99,  period: 'לחודש' },
  yearly:  { label: 'שנתי',  gross: 799, period: 'לשנה', highlight: true },
};

const LEGAL = [
  { id: 'terms',   title: 'תנאי שימוש',        content: 'השימוש בפלטפורמת SkySnap כפוף לתנאים אלה. המנוי מיועד לצלמי רחפן מקצועיים המעוניינים לקבל עבודות דרך הפלטפורמה. המנוי אישי ואינו ניתן להעברה. SkySnap שומרת לעצמה את הזכות להשעות חשבון שמפר את כללי הקהילה. כל התכנים המועלים על ידי הצלם הינם באחריותו הבלעדית.' },
  { id: 'cancel',  title: 'מדיניות ביטול עסקה', content: 'בהתאם לחוק הגנת הצרכן (ביטול עסקה), התשע״א-2010: ניתן לבטל עסקה בתוך 14 ימים מיום הרכישה, ובלבד שלא נעשה שימוש בשירות (לא הועלה תוכן ולא התקבלו הזמנות). הביטול יבוצע בפנייה בכתב לכתובת support@skysnap.co.il. לאחר ביטול תקין, הסכום יוחזר תוך 14 ימי עסקים לאמצעי התשלום המקורי, בניכוי דמי ביטול בשיעור 5% או ₪100, הנמוך מביניהם.' },
  { id: 'privacy', title: 'מדיניות פרטיות',     content: 'SkySnap מכבדת את פרטיות המשתמשים. המידע הנאסף (שם, אימייל, מיקום, תכנים שהועלו) משמש אך ורק לצורך מתן השירות. פרטי כרטיס האשראי מועברים ישירות לחברת הסליקה Cardcom ואינם נשמרים על שרתינו. בהתאם לחוק הגנת הפרטיות, תשמ״א-1981.' },
];

function CheckoutPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const planId = searchParams.get('plan') || 'monthly';
  const plan = PLAN_INFO[planId] || PLAN_INFO.monthly;

  const net = +(plan.gross / (1 + VAT_RATE)).toFixed(2);
  const vat = +(plan.gross - net).toFixed(2);

  const WC_URLS = {
    monthly: process.env.REACT_APP_WC_MONTHLY || 'https://3dreamsketcher.com/product/skysnap-monthly/',
    yearly:  process.env.REACT_APP_WC_YEARLY  || 'https://3dreamsketcher.com/product/skysnap-yearly/',
  };

  const [agreed, setAgreed] = useState({ terms: false, cancel: false });
  const [errors, setErrors] = useState({});

  if (!user) { navigate('/login'); return null; }

  const proceed = (e) => {
    e.preventDefault();
    const errs = {};
    if (!agreed.terms)  errs.terms  = 'יש לאשר את תנאי השימוש';
    if (!agreed.cancel) errs.cancel = 'יש לאשר את מדיניות הביטול';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    // Redirect to WooCommerce product page — payment handled there
    window.location.href = WC_URLS[planId];
  };

  /* ── PROCESSING (after redirect back from Cardcom success) ── */
  const paymentResult = new URLSearchParams(window.location.search).get('payment');

  return (
    <div style={{ background: 'var(--bg)', minHeight: 'calc(100vh - 60px)', padding: '2rem 1rem 5rem' }}>
      <div style={{ maxWidth: 860, margin: '0 auto' }}>

        <button onClick={() => navigate(-1)} className="btn btn-outline" style={{ marginBottom: '1.5rem', fontSize: '0.75rem' }}>← חזרה</button>
        <h1 style={{ fontFamily: 'Oxanium, monospace', fontWeight: 800, fontSize: '1.4rem', letterSpacing: '3px', textTransform: 'uppercase', color: 'var(--text-bright)', marginBottom: '1.75rem' }}>
          השלמת רכישה
        </h1>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: '2rem', alignItems: 'start' }}>

          {/* ── Left: agreements + proceed ── */}
          <form onSubmit={proceed} noValidate>

            {/* What you get */}
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 18, padding: '1.75rem', marginBottom: '1.25rem' }}>
              <div style={{ fontFamily: 'Oxanium, monospace', fontWeight: 700, fontSize: '0.78rem', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--text-bright)', marginBottom: '1rem' }}>מה כלול במנוי</div>
              {['העלאת תמונות וסרטונים ללא הגבלה', 'הופעה במפת הצלמים הזמינים', 'קבלת הזמנות מלקוחות דרך הפלטפורמה', 'דף פרופיל מקצועי עם פורטפוליו', 'תמיכה בעברית 7 ימים בשבוע'].map(f => (
                <div key={f} style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', marginBottom: '0.55rem', fontSize: '0.85rem', color: 'var(--text)' }}>
                  <span style={{ color: 'var(--success)', fontSize: '0.9rem', flexShrink: 0 }}>✓</span>{f}
                </div>
              ))}
            </div>

            {/* Legal agreements */}
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 18, padding: '1.75rem', marginBottom: '1.25rem' }}>
              <div style={{ fontFamily: 'Oxanium, monospace', fontWeight: 700, fontSize: '0.78rem', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--text-bright)', marginBottom: '1.25rem' }}>אישורים נדרשים</div>

              {[
                { key: 'terms',  text: <>קראתי ואני מסכים/ה ל<a href="#terms" style={{ color: 'var(--primary)', textDecoration: 'none' }}>תנאי השימוש</a> ול<a href="/skysnap/privacy" style={{ color: 'var(--primary)', textDecoration: 'none' }}>מדיניות הפרטיות</a> של SkySnap</> },
                { key: 'cancel', text: <>קראתי ואני מסכים/ה ל<a href="#cancel" style={{ color: 'var(--primary)', textDecoration: 'none' }}>מדיניות הביטול</a>: ביטול תוך 14 יום אם לא נעשה שימוש, החזר בניכוי 5% או ₪100</> },
              ].map(({ key, text }) => (
                <div key={key}>
                  <label style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', cursor: 'pointer', marginBottom: '0.85rem' }}>
                    <input type="checkbox" checked={agreed[key]}
                      onChange={e => { setAgreed(a => ({ ...a, [key]: e.target.checked })); setErrors(er => ({ ...er, [key]: '' })); }}
                      style={{ width: 18, height: 18, marginTop: 2, accentColor: 'var(--primary)', flexShrink: 0 }} />
                    <span style={{ fontSize: '0.82rem', color: 'var(--text)', lineHeight: 1.6 }}>{text}</span>
                  </label>
                  {errors[key] && <div style={{ color: 'var(--danger)', fontSize: '0.72rem', marginTop: -6, marginBottom: 8, fontFamily: 'Barlow Condensed, sans-serif' }}>{errors[key]}</div>}
                </div>
              ))}
            </div>

            {errors.submit && <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{errors.submit}</div>}

            {/* Security badge + submit */}
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 18, padding: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem', padding: '0.75rem', background: 'rgba(0,200,240,0.05)', borderRadius: 10, border: '1px solid var(--border)' }}>
                <span style={{ fontSize: '1.5rem' }}>🔒</span>
                <div>
                  <div style={{ fontFamily: 'Oxanium, monospace', fontWeight: 700, fontSize: '0.75rem', letterSpacing: '1px', color: 'var(--primary)' }}>תשלום מאובטח — WooCommerce</div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)', marginTop: 2 }}>תועבר לדף הרכישה המאובטח. לאחר התשלום, המנוי יופעל אוטומטית תוך דקה.</div>
                </div>
              </div>
              <button type="submit" className="btn btn-primary"
                style={{ width: '100%', padding: '1rem', fontSize: '0.9rem', borderRadius: 12 }}>
                🛒 המשך לתשלום ₪{plan.gross}
              </button>
              <div style={{ textAlign: 'center', marginTop: '0.75rem', fontSize: '0.68rem', color: 'var(--text-dim)', fontFamily: 'Barlow Condensed, sans-serif' }}>
                תועבר לדף הרכישה של האתר לביצוע התשלום
              </div>
            </div>
          </form>

          {/* ── Right: Order summary ── */}
          <div style={{ position: 'sticky', top: 80, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {/* Price summary */}
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 18, padding: '1.75rem' }}>
              <div style={{ fontFamily: 'Oxanium, monospace', fontWeight: 700, fontSize: '0.78rem', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--text-bright)', marginBottom: '1.25rem' }}>סיכום הזמנה</div>

              <div style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid var(--border)' }}>
                <div style={{ fontFamily: 'Oxanium, monospace', fontWeight: 800, color: 'var(--primary)', fontSize: '1rem', marginBottom: 4 }}>מנוי SkySnap {plan.label}</div>
                <div style={{ color: 'var(--text-dim)', fontSize: '0.75rem', fontFamily: 'Barlow Condensed, sans-serif' }}>העלאות · מפה · הזמנות</div>
              </div>

              {[
                { label: `מחיר לפני מע"מ`, val: `₪${net}`, dim: false },
                { label: `מע"מ 17%`,        val: `₪${vat}`, dim: true  },
              ].map(r => (
                <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: '0.82rem' }}>
                  <span style={{ color: r.dim ? 'var(--text-dim)' : 'var(--text)' }}>{r.label}</span>
                  <span style={{ fontFamily: 'Oxanium, monospace', color: r.dim ? 'var(--text-dim)' : 'var(--text)' }}>{r.val}</span>
                </div>
              ))}

              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.75rem', borderTop: '1px solid var(--border)' }}>
                <span style={{ fontFamily: 'Oxanium, monospace', fontWeight: 800, color: 'var(--text-bright)', fontSize: '0.9rem' }}>סה"כ לתשלום</span>
                <span style={{ fontFamily: 'Oxanium, monospace', fontWeight: 900, fontSize: '1.35rem', color: 'var(--primary)' }}>₪{plan.gross}</span>
              </div>
              <div style={{ fontSize: '0.65rem', color: 'var(--text-dim)', textAlign: 'left', marginTop: 2, fontFamily: 'Barlow Condensed, sans-serif' }}>כולל מע"מ</div>

              <div style={{ background: 'var(--surface-2)', borderRadius: 8, padding: '0.7rem 0.9rem', marginTop: '1rem', fontSize: '0.7rem', color: 'var(--text-dim)', lineHeight: 1.6 }}>
                💡 המנוי יתחדש אוטומטית ב-₪{plan.gross} {plan.period}.<br />ניתן לבטל בכל עת.
              </div>
            </div>

            {/* Business info */}
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '1.25rem', fontSize: '0.7rem', color: 'var(--text-dim)', lineHeight: 1.8 }}>
              <div style={{ fontFamily: 'Oxanium, monospace', fontWeight: 700, fontSize: '0.68rem', letterSpacing: '1.5px', textTransform: 'uppercase', color: 'var(--text-dim)', marginBottom: '0.6rem' }}>פרטי העסק</div>
              <div><strong style={{ color: 'var(--text)' }}>SkySnap</strong> — שירות צילומי רחפנים</div>
              <div>שירות לקוחות: <a href="mailto:support@skysnap.co.il" style={{ color: 'var(--primary)', textDecoration: 'none' }}>support@skysnap.co.il</a></div>
              <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid var(--border)', fontSize: '0.65rem' }}>
                סליקה: WooCommerce / WordPress · SSL 256-bit<br/>
                חוק הגנת הצרכן, תשמ"א-1981
              </div>
            </div>
          </div>
        </div>

        {/* Legal sections */}
        <div style={{ marginTop: '3rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {LEGAL.map(s => (
            <details key={s.id} id={s.id} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '0.9rem 1.25rem' }}>
              <summary style={{ fontFamily: 'Oxanium, monospace', fontWeight: 700, fontSize: '0.75rem', letterSpacing: '1.5px', textTransform: 'uppercase', color: 'var(--text-bright)', cursor: 'pointer', userSelect: 'none' }}>
                {s.title}
              </summary>
              <p style={{ marginTop: '0.75rem', fontSize: '0.8rem', color: 'var(--text-dim)', lineHeight: 1.8 }}>{s.content}</p>
            </details>
          ))}
        </div>

      </div>
    </div>
  );
}

// ============ PRIVACY PAGE ============

function PrivacyPage() {
  const s = { marginBottom: '2rem' };
  const h = { fontFamily: 'Oxanium, monospace', fontWeight: 700, fontSize: '1rem', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--primary)', marginBottom: '0.75rem' };
  const p = { color: 'var(--text)', fontSize: '0.9rem', lineHeight: 1.9 };
  const li = { color: 'var(--text)', fontSize: '0.9rem', lineHeight: 2, marginRight: '1.25rem' };

  return (
    <div style={{ background: 'var(--bg)', minHeight: 'calc(100vh - 60px)', padding: '3rem 1.5rem' }}>
      <div style={{ maxWidth: 780, margin: '0 auto' }}>

        <h1 style={{ fontFamily: 'Oxanium, monospace', fontWeight: 900, fontSize: 'clamp(1.6rem,4vw,2.4rem)', letterSpacing: '4px', textTransform: 'uppercase', color: 'var(--text-bright)', marginBottom: '0.5rem' }}>
          מדיניות פרטיות
        </h1>
        <p style={{ color: 'var(--text-dim)', fontSize: '0.8rem', fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '1px', marginBottom: '3rem' }}>
          עדכון אחרון: מאי 2026 · SkySnap — שירות צילומי רחפנים
        </p>

        <div style={s}>
          <h2 style={h}>1. מי אנחנו</h2>
          <p style={p}>SkySnap הוא שירות מקוון המחבר בין לקוחות לצלמי רחפנים מקצועיים בישראל. האתר פועל בכתובת <strong style={{ color: 'var(--primary)' }}>3dreamsketcher.com/skysnap</strong>. לפניות: <a href="mailto:support@skysnap.co.il" style={{ color: 'var(--primary)' }}>support@skysnap.co.il</a></p>
        </div>

        <div style={s}>
          <h2 style={h}>2. איזה מידע אנחנו אוספים</h2>
          <ul>
            <li style={li}><strong>פרטי חשבון:</strong> שם, כתובת אימייל, מספר טלפון (אם סיפקת)</li>
            <li style={li}><strong>תמונת פרופיל:</strong> תמונה שהעלית או שהתקבלה מ-Google</li>
            <li style={li}><strong>תוכן שהועלה:</strong> תמונות וסרטונים שהעלית לפלטפורמה</li>
            <li style={li}><strong>מיקום GPS:</strong> רק אם בחרת לצרף מיקום לפוסט, ורק המיקום שאישרת</li>
            <li style={li}><strong>נתוני הזמנות:</strong> כתובת הצילום, תאריך, הערות</li>
            <li style={li}><strong>נתוני תשלום:</strong> מעובדים על ידי WooCommerce בלבד — אנחנו לא רואים ולא שומרים פרטי כרטיס אשראי</li>
          </ul>
        </div>

        <div style={s}>
          <h2 style={h}>3. איך אנחנו משתמשים במידע</h2>
          <ul>
            <li style={li}>הפעלת שירות ההתאמה בין לקוחות לצלמים</li>
            <li style={li}>הצגת פרופילים ופורטפוליו ציבורי</li>
            <li style={li}>ניהול הזמנות ותשלומים</li>
            <li style={li}>שליחת הודעות WhatsApp (רק כאשר יזמת הזמנה, ורק לצלם שהזמנת)</li>
            <li style={li}>שיפור השירות על בסיס שימוש כללי ואנונימי</li>
          </ul>
        </div>

        <div style={s}>
          <h2 style={h}>4. שיתוף מידע עם צדדים שלישיים</h2>
          <p style={{ ...p, marginBottom: '0.75rem' }}>אנחנו משתמשים בשירותי צד שלישי הבאים:</p>
          <ul>
            <li style={li}><strong>MongoDB Atlas</strong> — אחסון נתונים (ארה"ב, תואם GDPR)</li>
            <li style={li}><strong>Cloudinary</strong> — אחסון תמונות וסרטונים</li>
            <li style={li}><strong>Railway.app</strong> — שרת האפליקציה</li>
            <li style={li}><strong>Google Sign-In</strong> — אימות זהות (אופציונלי)</li>
            <li style={li}><strong>WooCommerce / Cardcom</strong> — עיבוד תשלומים</li>
            <li style={li}><strong>OpenStreetMap</strong> — תצוגת מפות (ללא שיתוף נתונים אישיים)</li>
          </ul>
          <p style={{ ...p, marginTop: '0.75rem' }}>אנחנו לא מוכרים ולא משכירים את הנתונים שלך לאף גורם.</p>
        </div>

        <div style={s}>
          <h2 style={h}>5. אבטחת מידע</h2>
          <p style={p}>הנתונים מועברים בהצפנת SSL. סיסמאות מוצפנות עם bcrypt. טוקני JWT בתוקף של 7 ימים. אנחנו לא שומרים פרטי אמצעי תשלום על שרתינו.</p>
        </div>

        <div style={s}>
          <h2 style={h}>6. זכויותיך</h2>
          <p style={{ ...p, marginBottom: '0.75rem' }}>בהתאם לחוק הגנת הפרטיות, תשמ"א-1981 ותקנות GDPR:</p>
          <ul>
            <li style={li}>עיון במידע שנשמר עליך</li>
            <li style={li}>תיקון מידע שגוי — דרך לוח הבקרה</li>
            <li style={li}>מחיקת החשבון וכל הנתונים — דרך לוח הבקרה → "מחק חשבון"</li>
            <li style={li}>התנגדות לעיבוד מידע — פנה אלינו בכתב</li>
          </ul>
        </div>

        <div style={s}>
          <h2 style={h}>7. קבצי Cookie</h2>
          <p style={p}>האתר משתמש ב-localStorage לשמירת טוקן ההתחברות בלבד. אנחנו לא משתמשים ב-cookies לצרכי פרסום או מעקב.</p>
        </div>

        <div style={s}>
          <h2 style={h}>8. שינויים במדיניות</h2>
          <p style={p}>שינויים מהותיים יפורסמו באתר. המשך השימוש בשירות לאחר פרסום השינויים מהווה הסכמה אליהם.</p>
        </div>

        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '1.5rem', marginTop: '2rem' }}>
          <h2 style={{ ...h, color: 'var(--text-bright)' }}>צור קשר</h2>
          <p style={p}>לכל שאלה בנושא פרטיות: <a href="mailto:support@skysnap.co.il" style={{ color: 'var(--primary)' }}>support@skysnap.co.il</a></p>
        </div>

      </div>
    </div>
  );
}

// ============ CHAT WIDGET ============

const WHATSAPP_ICON_PATH = "M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0012.04 2zm0 18.13h-.01c-1.48 0-2.93-.4-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.18 8.18 0 01-1.26-4.36c0-4.54 3.7-8.24 8.25-8.24 2.2 0 4.27.86 5.83 2.42a8.19 8.19 0 012.41 5.83c0 4.55-3.7 8.21-8.23 8.21zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.17.25-.64.81-.78.97-.14.17-.29.18-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.12-.14.16-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.36-.77-1.86-.2-.49-.41-.42-.56-.43-.14-.01-.31-.01-.47-.01-.17 0-.43.06-.66.31-.23.25-.86.84-.86 2.05 0 1.21.88 2.38 1 2.54.12.17 1.74 2.66 4.22 3.73.59.25 1.05.4 1.41.52.59.19 1.13.16 1.55.1.47-.07 1.47-.6 1.68-1.19.21-.58.21-1.08.14-1.19-.06-.1-.23-.16-.48-.28z";

function getChatSessionId() {
  let id = localStorage.getItem('chatSessionId');
  if (!id) {
    id = Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem('chatSessionId', id);
  }
  return id;
}

function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', text: 'שלום! 👋 אני כאן לעזור עם שאלות על SkySnap — מחירים, אזורים, הזמנות ועוד. איך אפשר לעזור?' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const sessionIdRef = useRef(null);
  const messagesEndRef = useRef(null);

  if (!sessionIdRef.current) sessionIdRef.current = getChatSessionId();

  useEffect(() => {
    if (isOpen) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isOpen]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setMessages(m => [...m, { role: 'user', text }]);
    setInput('');
    setLoading(true);

    try {
      const res = await api.post('/chat', { sessionId: sessionIdRef.current, message: text });
      setMessages(m => [...m, { role: 'assistant', text: res.data.reply }]);
    } catch {
      setMessages(m => [...m, { role: 'assistant', text: 'מצטערים, אירעה שגיאה. נסה שוב 🙏' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {isOpen && (
        <div className="chat-window">
          <div className="chat-header">
            <span>💬 SkySnap — עזרה</span>
            <button className="chat-close" onClick={() => setIsOpen(false)} aria-label="סגור">✕</button>
          </div>
          <div className="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`chat-bubble ${m.role}`}>{m.text}</div>
            ))}
            {loading && <div className="chat-bubble assistant chat-typing">כותב...</div>}
            <div ref={messagesEndRef} />
          </div>
          <div className="chat-input-row">
            <input
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="כתוב הודעה..."
            />
            <button onClick={sendMessage} disabled={loading || !input.trim()}>שלח</button>
          </div>
        </div>
      )}
      <button
        className="whatsapp-float"
        onClick={() => setIsOpen(o => !o)}
        aria-label="צ'אט עזרה"
      >
        <svg viewBox="0 0 24 24">
          <path d={WHATSAPP_ICON_PATH}/>
        </svg>
      </button>
    </>
  );
}

// ============ MAIN APP ============

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing token
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      setUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      <Router basename={process.env.REACT_APP_BASENAME || '/'}>
        <div className="app">
          <Navbar />
          <BookingPaidHandler />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/map" element={<MapPage />} />
            <Route path="/portfolio" element={<Navigate to="/feed" />} />
            <Route path="/feed" element={<FeedPage />} />
            <Route path="/profiles" element={<ProfilesPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/profile/:id" element={<PhotographerProfilePage />} />
            <Route path="/checkout" element={user ? <CheckoutPage /> : <Navigate to="/login" />} />
            <Route path="/dashboard" element={user ? <Dashboard /> : <Navigate to="/login" />} />
          </Routes>
          <ChatWidget />
        </div>
      </Router>
    </AuthContext.Provider>
  );
}

export default App;